import React, { useState, useEffect, useRef } from 'react';
import { ChevronRight, FileText, Download, AlertTriangle, Share2 } from 'lucide-react';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from 'recharts';
import QRCode from 'qrcode';
import { activateMembershipCode, checkFeatureTrialAccess, getStoredUser, isProMember } from '../lib/api';
import EnneagramTest from './EnneagramTest';
import BeautyFaceTest from './BeautyFaceTest';
import MbtiStageTest from './MbtiStageTest';
import XuanYuanReportFooter from './XuanYuanReportFooter';
import XuanYuanTopBar from './XuanYuanTopBar';
import {
  SBTI_QUESTIONS,
  SBTI_DIMENSIONS,
  SBTI_DRINK_GATE_ID,
  SBTI_DRINK_TRIGGER_ID,
  SBTI_TYPE_IMAGES,
  SBTI_DIMENSION_ORDER,
  computeSbtiResult,
  createSbtiQuestionFlow,
  withDrinkTriggerIfNeeded,
  type SbtiQuestion,
  type SbtiResult,
} from '../data/sbtiData';

const SITE_URL = 'https://qimen.aifashou.cn';
const SBTI_SHARE_URL = `${SITE_URL}/?tool=psychSbti&tab=practice`;
const WECHAT_ID = 'dao6257';
const PSY_TRIAL_GROUP_KEY = 'psych_tests_bundle';

// --- 工具函数：生成标准选项 ---
const getLikertOptions = (texts: string[], trait: string, reverse = false, maxScore = 5) => {
  return texts.map((text, idx) => {
    const scoreVal = reverse ? (idx + 1) : (maxScore - idx);
    return { text, score: { [trait]: scoreVal } };
  });
};

const LIKERT_5 = ['非常符合', '比较符合', '一般', '不太符合', '完全不符'];
const qL = (text: string, trait: string, reverse = false) => ({ text, options: getLikertOptions(LIKERT_5, trait, reverse, 5) });

// --- MBTI 原始数据配置 ---
const MBTI_DATA = {
  questions: [
    { id: 1, question: '当你某日想去一个地方,你会____', optionA: '事先计划好了，然后再去', optionB: '先去，然后随机应变' },
    { id: 2, question: '如果你是一位老师,你愿教____', optionA: '注重实践的课程', optionB: '注重理论的课程' },
    { id: 3, question: '遇到问题时，你通常喜欢_____', optionA: '和别人讨论解决方法', optionB: '自己想办法解决' },
    { id: 4, question: '你认为____', optionA: '很早就应该开始为聚会，约会等做准备', optionB: '不必先做准备，去了以后见机行事' },
    { id: 5, question: '你通常和____相处得更好', optionA: '喜欢想象的人', optionB: '注重现实的人' },
    { id: 6, question: '你更多时候是____', optionA: '让情感驾驭理智', optionB: '让理智驾驭情感' },
    { id: 7, question: '当你和一群人在一起时，你常常是更愿意____', optionA: '加入到大家的谈话中去', optionB: '独自和熟识的人交谈' },
    { id: 8, question: '你最喜欢____做事情', optionA: '按兴致', optionB: '按计划' },
    { id: 9, question: '你希望自己被看作是一个____', optionA: '实干家', optionB: '发明家' },
    { id: 10, question: '当别人问你一个问题时，你经常会____', optionA: '马上就做回答', optionB: '先在脑子里想一想' },
    { id: 11, question: '你喜欢与____打交道', optionA: '常有出人意料想法的人', optionB: '按照常理行事的人' },
    { id: 12, question: '按日程表办事____', optionA: '正合你意', optionB: '束缚了你' },
    { id: 13, question: '你觉得通常别人____', optionA: '要花很长的时间才能和你相熟', optionB: '很快就能和你熟识' },
    { id: 14, question: '为如何过周末订一个计划____', optionA: '是有必要的', optionB: '完全没必要' },
    { id: 15, question: '下列哪一个评价更适合你', optionA: '性情中人', optionB: '理智的人' },
    { id: 16, question: '更多的时候，你倾向于____', optionA: '独处', optionB: '同他人在一起' },
    { id: 17, question: '在日常工作中，你更喜欢____', optionA: '在时间紧迫的情况下，争分夺秒地工作', optionB: '做好提前量，尽早把工作做完' },
    { id: 18, question: '你更愿把____作为朋友', optionA: '总能有新想法的人', optionB: '脚踏实地的人' },
    { id: 19, question: '你是一个____', optionA: '兴趣广泛，什么都想尝试的人', optionB: '专注的投入某个兴趣的人' },
    { id: 20, question: '当你有一项特别的工作要做时，你喜欢先____', optionA: '察看到工作的全貌', optionB: '找出必须要做的环节' },
    { id: 21, question: '你更接受____', optionA: '以情动人', optionB: '以理服人' },
    { id: 22, question: '当你为了消遣而阅读时，你____', optionA: '欣赏作者奇特、独创的表达', optionB: '喜欢作者的表达直接、明确' },
    { id: 23, question: '新认识你的人____了解到你的兴趣所在', optionA: '马上就能', optionB: '只有真正和你熟悉以后才能' },
    { id: 24, question: '在旅行时，你喜欢____', optionA: '随兴致行事', optionB: '事先知道一天中该做的事' },
    { id: 25, question: '做许多人都做的事时，你喜欢____', optionA: '按惯例去做', optionB: '发明自己的新方法' },
    { id: 26, question: '多数人说你是一个____', optionA: '不爱吐露心事的人', optionB: '非常坦率的人' },
    { id: 27, question: '你更倾向或喜欢：(指你现在已具有的，而非向往的)', optionA: '看不见的', optionB: '看的见的' },
    { id: 28, question: '你更倾向或喜欢：', optionA: '计划', optionB: '随意' },
    { id: 29, question: '你更倾向或喜欢：', optionA: '温情', optionB: '坚定' },
    { id: 30, question: '你更倾向或喜欢：', optionA: '事实', optionB: '想法' },
    { id: 31, question: '你更倾向或喜欢：', optionA: '思维', optionB: '情感' },
    { id: 32, question: '你更倾向或喜欢：', optionA: '热忱', optionB: '平静' },
    { id: 33, question: '你更倾向或喜欢：', optionA: '说服', optionB: '打动' },
    { id: 34, question: '你更倾向或喜欢：', optionA: '陈述', optionB: '概念' },
    { id: 35, question: '你更倾向或喜欢：', optionA: '分析', optionB: '同情' },
    { id: 36, question: '你更倾向或喜欢：', optionA: '系统性', optionB: '随机性' },
    { id: 37, question: '你更倾向或喜欢：', optionA: '敏感', optionB: '精确' },
    { id: 38, question: '你更倾向或喜欢：', optionA: '缄默', optionB: '健谈' },
    { id: 39, question: '你更倾向或喜欢：', optionA: '常识性的', optionB: '理论性的' },
    { id: 40, question: '你更倾向或喜欢：', optionA: '侠肝义胆', optionB: '深谋远虑' },
    { id: 41, question: '你更倾向或喜欢：', optionA: '正式', optionB: '非正式' },
    { id: 42, question: '你更倾向或喜欢：', optionA: '沉静', optionB: '活跃' },
    { id: 43, question: '你更倾向或喜欢：', optionA: '利益', optionB: '祝福' },
    { id: 44, question: '你更倾向或喜欢：', optionA: '理论性', optionB: '确定性' },
    { id: 45, question: '你更倾向或喜欢：', optionA: '坚定的', optionB: '忠诚的' },
    { id: 46, question: '你更倾向或喜欢：', optionA: '理想', optionB: '现实' },
    { id: 47, question: '你更倾向或喜欢：', optionA: '雄心', optionB: '柔肠' },
    { id: 48, question: '你更倾向或喜欢：', optionA: '想象中的', optionB: '事实上的' },
    { id: 49, question: '你更倾向或喜欢：', optionA: '冷静的', optionB: '激情的' },
    { id: 50, question: '你更倾向或喜欢：', optionA: '制作', optionB: '创造' },
    { id: 51, question: '你更倾向或喜欢：', optionA: '热情的', optionB: '中立的' },
    { id: 52, question: '你更倾向或喜欢：', optionA: '明理的', optionB: '迷人的' },
    { id: 53, question: '你更倾向或喜欢：', optionA: '有同情心', optionB: '有逻辑头脑' },
    { id: 54, question: '你更倾向或喜欢：', optionA: '生产', optionB: '设计' },
    { id: 55, question: '你更倾向或喜欢：', optionA: '冲动', optionB: '决择' },
    { id: 56, question: '你更倾向或喜欢：', optionA: '公正的', optionB: '体谅的' },
    { id: 57, question: '你更倾向或喜欢：', optionA: '安静的', optionB: '爱交际的' },
    { id: 58, question: '你更倾向或喜欢：', optionA: '理性', optionB: '感性' },
    { id: 59, question: '你更倾向或喜欢：', optionA: '不受限制的', optionB: '安排好的' },
    { id: 60, question: '你更倾向或喜欢：', optionA: '具体', optionB: '抽象' },
    { id: 61, question: '你更倾向或喜欢：', optionA: '能干的', optionB: '细腻的' },
    { id: 62, question: '你更倾向或喜欢：', optionA: '开放', optionB: '私密' },
    { id: 63, question: '你更倾向或喜欢：', optionA: '建造', optionB: '发明' },
    { id: 64, question: '你更倾向或喜欢：', optionA: '有序的', optionB: '随便的' },
    { id: 65, question: '你更倾向或喜欢：', optionA: '想象', optionB: '现实' },
    { id: 66, question: '你更倾向或喜欢：', optionA: '好胜的', optionB: '好心的' },
    { id: 67, question: '你更倾向或喜欢：', optionA: '理论', optionB: '事实' },
    { id: 68, question: '你更倾向或喜欢：', optionA: '很少的朋友', optionB: '很多的朋友' },
    { id: 69, question: '你更倾向或喜欢：', optionA: '可能', optionB: '确知' },
    { id: 70, question: '你更倾向或喜欢：', optionA: '宽容的', optionB: '坚决的' },
    { id: 71, question: '你更倾向或喜欢：', optionA: '新异的', optionB: '已知的' },
    { id: 72, question: '你更倾向或喜欢：', optionA: '温柔', optionB: '力量' },
    { id: 73, question: '你更倾向或喜欢：', optionA: '实用', optionB: '创新' },
    { id: 74, question: '和一群人在一起聚会通常会让你感到____', optionA: '兴致勃勃', optionB: '筋疲力尽' },
    { id: 75, question: '你在做一个决定时，更多地会____', optionA: '权衡实际的得失', optionB: '考虑其他人的感受' },
    { id: 76, question: '通常你更喜欢____', optionA: '提前安排好该做什么', optionB: '到时候率性而为' },
    { id: 77, question: '当你一个人在家时，你____', optionA: '能够沉静在自己的思维中', optionB: '总觉的应该做点什么事情' },
    { id: 78, question: '多数情况下，你____', optionA: '随兴致做事', optionB: '按日程表做事' },
    { id: 79, question: '你通常____', optionA: '容易和大家打成一片', optionB: '独处的时候更多' },
    { id: 80, question: '你做事更倾向于____', optionA: '等到各方面的信息都全了以后再做计划', optionB: '提前很久就定计划' },
    { id: 81, question: '别人____交上朋友', optionA: '容易和你', optionB: '较难和你' },
    { id: 82, question: '你通常喜欢上____的课程', optionA: '探讨理论和概念', optionB: '列举事实和图表' },
    { id: 83, question: '在聚会时，你____', optionA: '说的时候多', optionB: '听的时候多' },
    { id: 84, question: '你觉的自己更倾向于是一个____', optionA: '随意的人', optionB: '有秩序的人' },
    { id: 85, question: '你____', optionA: '只同那些兴趣相同的人才能长谈', optionB: '只要愿意，和任何人都可以长聊' },
    { id: 86, question: '当你有一个报告需要在一个星期内交出时，你____', optionA: '常留出足够的时间并能提早完成', optionB: '常常是在最后一刻及时赶出来' },
    { id: 87, question: '哪一个对你来说是更高的评价', optionA: '有好胜心的', optionB: '有同情心的' },
    { id: 88, question: '你觉得按日程表办事____', optionA: '虽有必要，但不喜欢', optionB: '有帮助的，非常喜欢' },
    { id: 89, question: '你更愿在一个____的老板手下工作', optionA: '态度亲切，但有时会感情用事', optionB: '态度严厉，但始终按逻辑办事' },
    { id: 90, question: '在完成一项大任务时，你常常是____', optionA: '边做边考虑下一步', optionB: '事先想好每个步骤' },
    { id: 91, question: '在社交场合，你通常觉得____', optionA: '很难和不认识的人进行交谈', optionB: '很容易和多数人谈笑风生' },
    { id: 92, question: '你常常是____', optionA: '按已经有效的方法做事', optionB: '尝试一下有没有更好的办法' },
    { id: 93, question: '你更喜欢按____做事情', optionA: '当天的感觉', optionB: '已订好的日程表' },
  ],
  scoring: {
    EI: { E_A: [3, 7, 10, 19, 23, 32, 62, 74, 79, 81, 83], E_B: [13, 16, 26, 38, 42, 57, 68, 77, 85, 91] },
    SN: { S_A: [2, 9, 25, 30, 34, 39, 50, 52, 54, 60, 63, 73, 92], S_B: [5, 11, 18, 22, 27, 44, 46, 48, 65, 67, 69, 71, 82] },
    TF: { T_A: [31, 33, 35, 43, 45, 47, 49, 56, 58, 61, 66, 75, 87], T_B: [6, 15, 21, 29, 37, 40, 51, 53, 70, 72, 89] },
    JP: { J_A: [1, 4, 12, 14, 20, 28, 36, 41, 64, 76, 86], J_B: [8, 17, 24, 55, 59, 78, 80, 84, 88, 90, 93] },
  },
  types: {
    ISTJ: { name: '物流师', desc: '内向-实感-思考-判断，严谨可靠，逻辑清晰，是坚定的基石。' },
    ISFJ: { name: '守卫者', desc: '内向-实感-情感-判断，忠诚体贴，默默付出，温柔且坚定。' },
    INFJ: { name: '提倡者', desc: '内向-直觉-情感-判断，富有洞察力，理想主义，深邃的灵魂伴侣。' },
    INTJ: { name: '建筑师', desc: '内向-直觉-思考-判断，富有战略，独立思考，极具前瞻性规划能力。' },
    ISTP: { name: '鉴赏家', desc: '内向-实感-思考-知觉，灵活务实，动手能力强，大胆的探索者。' },
    ISFP: { name: '探险家', desc: '内向-实感-情感-知觉，艺术敏感，随性自由，极具魅力的艺术家。' },
    INFP: { name: '调停者', desc: '内向-直觉-情感-知觉，理想主义，诗意浪漫，永远在寻找内心的宁静。' },
    INTP: { name: '逻辑学家', desc: '内向-直觉-思考-知觉，分析创新，求知若渴，脑洞大开的发明家。' },
    ESTP: { name: '企业家', desc: '外向-实感-思考-知觉，精力充沛，敢于冒险，是天生的问题解决专家。' },
    ESFP: { name: '表演者', desc: '外向-实感-情感-知觉，热情活力，魅力四射，永远是人群中的焦点。' },
    ENFP: { name: '竞选者', desc: '外向-直觉-情感-知觉，热情创意，自由洒脱，极具感染力的精神领袖。' },
    ENTP: { name: '辩论家', desc: '外向-直觉-思考-知觉，聪明好奇，思维敏捷，享受挑战一切不可能。' },
    ESTJ: { name: '总经理', desc: '外向-实感-思考-判断，高效务实，出色的组织者和绝对的领导者。' },
    ESFJ: { name: '执政官', desc: '外向-实感-情感-判断，乐于助人，极具责任心，是最受欢迎的倾听者。' },
    ENFJ: { name: '主人公', desc: '外向-直觉-情感-判断，富有感染力，天生具有号召力的精神导师。' },
    ENTJ: { name: '指挥官', desc: '外向-直觉-思考-判断，领导力强，雷厉风行，永远掌控全局的统帅。' },
  },
};

// SCL-90 原始题目与因子映射
const SCL90_RAW = [
  '头痛', '神经过敏，心中不踏实', '头脑中有不必要的想法', '头昏或昏倒', '对异性的兴趣减退', '对旁人责备求全', '感到别人能控制你的思想', '责怪别人制造麻烦', '忘记性大', '担心自己的衣饰整齐及仪态的端庄',
  '容易烦恼或激动', '胸痛', '害怕空旷的场所或街道', '感到自己的精力下降或活动减慢', '想结束自己的生命', '听到旁人听不到的声音', '发抖', '感到大多数人都不可信任', '胃口不好', '容易哭泣',
  '同异性相处时感到害羞或不自在', '感到受骗，中了圈套或有人想抓你', '无缘无故地突然感到害怕', '自己不能控制地大发脾气', '怕单独出门', '经常责怪自己', '腰痛', '感到难以完成任务', '感到孤独', '感到苦闷',
  '过分担忧', '对事物不感兴趣', '感到害怕', '我的感情容易受到伤害', '旁人能知道你的私下想法', '感到别人不理解你不同情你', '感到人们对你不友好，不喜欢你', '做事必须做的很慢以保证做的正确', '心跳的很厉害', '恶心或胃部不舒服',
  '感到比不上他人', '肌肉酸痛', '感到有人在监视你谈论你', '难以入睡', '做事必须反复检查', '难以做出决定', '怕乘地铁、公交车、电车或火车', '呼吸有困难', '一阵阵发冷或发热', '因为感到害怕而避开某些东西',
  '脑子变空了', '身体发麻或刺痛', '喉咙有梗塞感', '感到对前途没有希望', '不能集中注意力', '感到身体的某一部分软弱无力', '感到紧张或容易紧张', '感到手或脚发沉', '想到有关死亡的事', '吃的太多',
  '当别人看到你或谈论你时感到不自在', '有一些不属于你自己的想法', '有想打人或伤害他人的冲动', '醒的太早', '必须反复洗手、点数目或触摸某些东西', '睡得不稳不深', '有想摔坏或破坏东西的冲动', '有一些别人没有的想法或念头', '感到对别人神经过敏', '在商店或电影院等人多的地方感到不自在',
  '感到任何事情都很难做', '一阵阵恐惧或惊恐', '感到在公共场合吃东西很不舒服', '经常与人争论', '单独一人时神经很紧张', '别人对你的成绩没有做出恰当的评价', '即使和别人在一起也感到孤独', '感到坐立不安心神不宁', '感到自己没有什么价值', '感到熟悉的东西变成陌生或不像是真的',
  '大叫或摔东西', '害怕会在公共场合昏倒', '感到别人想占你的便宜', '为一些有关「性」的想法而苦恼', '认为应该因为自己的过错而受到惩罚', '感到要赶快把事情做完', '感到自己的身体有严重问题', '从未感到和其他人很亲近', '感到自己有罪', '感到自己脑子有病',
];
const scl90Map = [
  'f1', 'f5', 'f2', 'f1', 'f4', 'f3', 'f9', 'f8', 'f2', 'f2',
  'f6', 'f1', 'f7', 'f4', 'f4', 'f9', 'f5', 'f8', 'oth', 'f4',
  'f3', 'f4', 'f5', 'f6', 'f7', 'f4', 'f1', 'f2', 'f4', 'f4',
  'f4', 'f4', 'f5', 'f3', 'f9', 'f3', 'f3', 'f2', 'f5', 'f1',
  'f3', 'f1', 'f8', 'oth', 'f2', 'f2', 'f7', 'f1', 'f1', 'f7',
  'f2', 'f1', 'f1', 'f4', 'f2', 'f1', 'f5', 'f1', 'oth', 'oth',
  'f3', 'f9', 'f6', 'oth', 'f2', 'oth', 'f6', 'f8', 'f3', 'f7',
  'f4', 'f5', 'f3', 'f6', 'f7', 'f8', 'f9', 'f5', 'f4', 'f5',
  'f6', 'f7', 'f8', 'f9', 'f9', 'f5', 'f9', 'f9', 'oth', 'f9',
];

const SEX_REPRESS_RAW = [
  '谈论与性相关的话题时，我会感到不自在', '对性相关的内容容易产生羞耻感', '我认为性是私密且不应公开讨论的', '性幻想会让我感到愧疚', '在亲密关系中表达性需求让我为难',
  '我认为过于开放地谈论性是不恰当的', '对性相关广告或画面会本能回避', '性教育话题让我感到尴尬', '表达身体欲望时会有心理障碍', '将性与爱情分离让我难以接受',
  '对自身性吸引力缺乏自信', '性话题在社交场合让我不适', '对身体接触的尺度比较保守', '性相关讨论会引发焦虑', '对婚前性行为持保守态度',
  '性欲强会让我感到羞耻', '难以与伴侣坦诚讨论性偏好', '性幻想内容让我有负罪感', '对「性」这个字眼本身敏感', '认为性需求应该被压抑',
  '在发生亲密接触后，我经常会有一种莫名的空虚或自我厌恶感', '我潜意识里觉得享受肉体快感是一件堕落的事情', '即使在私密空间，我也必须关灯或在被子下才能感到安全', '我极度害怕别人发现我购买或浏览过任何成人用品/内容', '在伴侣面前裸露身体会让我感到极度缺乏安全感',
  '我总是被动地接受亲密行为，极少或从未主动发起过', '如果脑海中闪过违背常规的性冲动，我会立刻强迫自己停下', '我倾向于用忙碌的工作或学习来刻意转移自己的生理需求', '我很难在亲密互动中完全放松大脑，总是有各种道德评判的声音', '我认为人类应该追求更高尚的精神连接，而不是沉溺于原始的动物本能',
];

const QUIRK_RAW = [
  { q: '看到不对称的东西', a: '一定要摆正', b: '无所谓' }, { q: '听到某段旋律', a: '会一直反复哼', b: '很快就忘' }, { q: '摆放物品时', a: '必须有固定顺序', b: '随意即可' },
  { q: '睡前', a: '必须检查门锁多次', b: '检查一次就够' }, { q: '对数字', a: '有特别的偏好或忌讳', b: '没有特别感觉' }, { q: '做事时', a: '必须按固定流程', b: '可以灵活调整' },
  { q: '听到指甲刮黑板', a: '极度不适', b: '能忍受' }, { q: '对重复的图案', a: '会不自觉数数', b: '不会' }, { q: '走路时', a: '会刻意避免踩线', b: '不会注意' },
  { q: '关灯后', a: '会想象恐怖画面', b: '能平静入睡' }, { q: '看到密集小孔', a: '会感到不适', b: '没感觉' }, { q: '对某些材质', a: '有强烈的厌恶', b: '没有' },
  { q: '吃东西时', a: '必须把最喜欢的留到最后', b: '想吃哪个吃哪个' }, { q: '看到别人的错别字', a: '强迫症发作想纠正', b: '能看懂就行' }, { q: '坐电梯时', a: '喜欢站在特定的角落', b: '哪里空站哪里' },
  { q: '洗澡时', a: '清洗身体的顺序永远固定', b: '每次都很随意' }, { q: '出门前', a: '总觉得忘了带什么东西', b: '拿上包就走' }, { q: '看视频时', a: '音量必须调到偶数或特定的数字', b: '听得清就行' },
  { q: '别人碰到我的私人物品', a: '心里会极度不舒服', b: '觉得无所谓' }, { q: '写字或打字时', a: '一定要对称或对齐', b: '比较随意' }, { q: '上下楼梯', a: '总是不自觉在心里默数台阶数', b: '从没注意过' },
  { q: '手机上的红点提示', a: '必须全部点掉清除', b: '攒了几千个也不管' }, { q: '定闹钟', a: '喜欢定像7:03这种奇怪的时间', b: '总是定整点或半点' }, { q: '发呆时', a: '喜欢咬嘴唇或撕手皮', b: '什么都不做' },
];

const QUIRK_DIMENSIONS = [
  { key: 'order_symmetry', label: '对称强迫', simple: '强迫症倾向' },
  { key: 'repeat_check', label: '反复检查', simple: '反复确认' },
  { key: 'ritual_dependency', label: '仪式依赖', simple: '仪式感依赖' },
  { key: 'number_attachment', label: '数字执念', simple: '数字强迫' },
  { key: 'sensory_sensitive', label: '感官敏感', simple: '感官过敏' },
  { key: 'contamination_avoid', label: '洁净边界', simple: '洁癖倾向' },
  { key: 'privacy_guard', label: '私域边界', simple: '边界敏感' },
  { key: 'control_need', label: '控制需求', simple: '控制倾向' },
  { key: 'intrusive_imagery', label: '焦虑联想', simple: '焦虑联想' },
  { key: 'clearance_urge', label: '清零冲动', simple: '清零强迫' },
  { key: 'micro_habit', label: '小动作依赖', simple: '习惯动作' },
  { key: 'accuracy_fixation', label: '纠错执着', simple: '纠错强迫' },
] as const;

const QUIRK_DIMENSION_BY_INDEX = [
  'order_symmetry',
  'micro_habit',
  'ritual_dependency',
  'repeat_check',
  'number_attachment',
  'control_need',
  'sensory_sensitive',
  'accuracy_fixation',
  'ritual_dependency',
  'intrusive_imagery',
  'sensory_sensitive',
  'contamination_avoid',
  'control_need',
  'accuracy_fixation',
  'privacy_guard',
  'contamination_avoid',
  'repeat_check',
  'number_attachment',
  'privacy_guard',
  'order_symmetry',
  'micro_habit',
  'clearance_urge',
  'clearance_urge',
  'micro_habit',
] as const;

const PREFERENCE_TRAITS = [
  { key: 'abuse', label: '虐待' },
  { key: 'possession', label: '占有' },
  { key: 'pure_love', label: '纯爱' },
  { key: 'ntr', label: 'NTR' },
  { key: 'voyeur_guard', label: '窥探保护' },
  { key: 'rebel', label: '叛逆' },
  { key: 'open', label: '开放' },
  { key: 'forced', label: '被迫' },
  { key: 'coercive', label: '强迫' },
  { key: 'trans', label: '跨性' },
  { key: 'fetish', label: '恋物' },
  { key: 'roleplay', label: '扮演' },
  { key: 'humiliated', label: '受辱' },
  { key: 'humiliation', label: '羞辱' },
  { key: 'same_sex_group', label: '同性群体' },
  { key: 'exposure', label: '暴露' },
  { key: 'submission', label: '臣服' },
  { key: 'conquest', label: '征服' },
  { key: 'obedience_control', label: '服从控制' },
  { key: 'masochism', label: '受虐' },
] as const;

const BIPOLAR_RAW = [
  '我的情绪会在高涨和低落之间剧烈波动', '有一段时间会特别精力充沛、不需要睡', '有时会感到思维飞驰、想法一个接一个', '会有段时间话特别多、停不下来', '情绪高涨时会有冲动消费或冒险行为',
  '有时会过度自信、觉得无所不能', '会经历一段时间的情绪低落、对什么都提不起兴趣', '低落时睡眠会明显增多或减少', '情绪波动影响了我的工作或学习', '会有无缘无故的兴奋或烦躁',
  '有时精力旺盛到无法静坐', '低落时会觉得自己毫无价值', '情绪变化似乎没有明显诱因', '高涨和低落会周期性地出现', '曾因情绪问题影响人际关系',
  '睡眠需求在不同时期差异很大', '有时会同时感到兴奋和焦虑', '情绪波动持续数天或更长', '低落时难以集中注意力', '高涨时做事效率异常高',
  '会突然从低落变为兴奋', '情绪波动让我难以预测自己的状态', '在亢奋期，我会给很多人打电话发消息，表现得极其外向', '在低落期，我甚至连洗澡、吃饭等基本生活自理都觉得极其困难', '有时候我会觉得大脑像超负荷运转的发动机，完全停不下来',
  '我会因为一点极小的挫折就瞬间陷入绝望的深渊', '亢奋时，我会制定许多极其宏大但不切实际的计划', '身边的人曾对我说过，我有时候像变了个人一样', '情绪极端时，我会有自我伤害或结束一切的念头', '我感觉自己的人生就像在坐一辆没有刹车的过山车',
];

// city, dark, talent, needs, role, abo 题目
const CITY_QUESTIONS = [
  { text: '周末比起宅在家里，我更喜欢去探索城市里的新开的展览或独立书店。', options: [{ text: '非常符合', score: { culture: 5, modern: 2 } }, { text: '比较符合', score: { culture: 4 } }, { text: '一般', score: { culture: 3 } }, { text: '不太符合', score: { comfort: 4 } }, { text: '完全不符', score: { comfort: 5 } }] },
  { text: '我喜欢四季分明的气候，尤其是冬天能看到大雪纷飞。', options: [{ text: '非常符合', score: { north: 5 } }, { text: '比较符合', score: { north: 4 } }, { text: '无所谓', score: { north: 3, south: 3 } }, { text: '不太符合', score: { south: 4 } }, { text: '完全不符', score: { south: 5 } }] },
  { text: '为了更高的薪水和广阔的职业前景，我愿意忍受较长的通勤和高强度的加班。', options: [{ text: '非常符合', score: { strive: 5, modern: 3 } }, { text: '比较符合', score: { strive: 4 } }, { text: '看情况', score: { strive: 3, comfort: 3 } }, { text: '不太符合', score: { comfort: 4 } }, { text: '完全不符', score: { comfort: 5 } }] },
  { text: '吃饭对我来说是一项重要的社交活动，我喜欢热气腾腾、热闹甚至有点嘈杂的江湖馆子。', options: [{ text: '非常符合', score: { social: 5, spicy: 4 } }, { text: '比较符合', score: { social: 4, spicy: 3 } }, { text: '一般', score: { social: 3 } }, { text: '不太符合', score: { culture: 4, light: 3 } }, { text: '完全不符', score: { culture: 5, light: 5 } }] },
  { text: '走在有着几百年历史的古城墙或老弄堂里，会让我感到一种莫名的归属感。', options: [{ text: '非常符合', score: { history: 5, culture: 3 } }, { text: '比较符合', score: { history: 4 } }, { text: '一般', score: { history: 3 } }, { text: '不太符合', score: { modern: 4 } }, { text: '完全不符', score: { modern: 5 } }] },
  { text: '我的肠胃和味蕾极度依赖辣椒，无辣不欢。', options: [{ text: '非常符合', score: { spicy: 5 } }, { text: '比较符合', score: { spicy: 4 } }, { text: '一般', score: { spicy: 3, light: 3 } }, { text: '不太符合', score: { light: 4 } }, { text: '完全不符', score: { light: 5 } }] },
  { text: '我希望我居住的城市高度国际化，街上能看到各种肤色的人，随时能吃到正宗的异国料理。', options: [{ text: '非常符合', score: { diverse: 5, modern: 4 } }, { text: '比较符合', score: { diverse: 4 } }, { text: '一般', score: { diverse: 3 } }, { text: '不太符合', score: { history: 4 } }, { text: '完全不符', score: { history: 5, culture: 2 } }] },
  { text: '相比于拥挤的高效地铁，我更享受在满是梧桐树荫的街道上骑行或漫步的通勤方式。', options: [{ text: '非常符合', score: { comfort: 5, culture: 4 } }, { text: '比较符合', score: { comfort: 4 } }, { text: '一般', score: { comfort: 3, strive: 3 } }, { text: '不太符合', score: { strive: 4, modern: 3 } }, { text: '完全不符', score: { strive: 5, modern: 5 } }] },
  { text: '当我压力大时，我倾向于去山里、湖边或海边，用大自然来治愈自己。', options: [{ text: '非常符合', score: { nature: 5, comfort: 3 } }, { text: '比较符合', score: { nature: 4 } }, { text: '一般', score: { nature: 3 } }, { text: '不太符合', score: { social: 4 } }, { text: '完全不符', score: { social: 5, modern: 3 } }] },
  { text: '我非常看重人际交往中的"边界感"，反感邻里之间过分的打听和熟络。', options: [{ text: '非常符合', score: { modern: 5, strive: 3 } }, { text: '比较符合', score: { modern: 4 } }, { text: '一般', score: { modern: 3, social: 3 } }, { text: '不太符合', score: { social: 4, north: 2 } }, { text: '完全不符', score: { social: 5, north: 4 } }] },
  { text: '城市的医疗、教育等顶级硬核资源，是我选择定居的首要考量因素。', options: [{ text: '非常符合', score: { history: 3, strive: 4, north: 2 } }, { text: '比较符合', score: { strive: 3 } }, { text: '一般', score: { strive: 2 } }, { text: '不太符合', score: { comfort: 3 } }, { text: '完全不符', score: { comfort: 5, nature: 2 } }] },
  { text: '我喜欢气候常年温润、湿度较高的环境，讨厌皮肤干燥开裂的感觉。', options: [{ text: '非常符合', score: { south: 5 } }, { text: '比较符合', score: { south: 4 } }, { text: '一般', score: { south: 3, north: 3 } }, { text: '不太符合', score: { north: 4 } }, { text: '完全不符', score: { north: 5 } }] },
  { text: '休息日我宁愿花几百块喝杯精品咖啡，也不想去嘈杂的菜市场感受烟火气。', options: [{ text: '非常符合', score: { modern: 5, culture: 2 } }, { text: '比较符合', score: { modern: 4 } }, { text: '一般', score: { modern: 3 } }, { text: '不太符合', score: { social: 4, comfort: 4 } }, { text: '完全不符', score: { social: 5 } }] },
  { text: '比起一眼望到头的老家，我更向往充满不确定性和挑战的超级都市。', options: [{ text: '非常符合', score: { strive: 5, diverse: 3 } }, { text: '比较符合', score: { strive: 4 } }, { text: '一般', score: { strive: 3 } }, { text: '不太符合', score: { comfort: 4 } }, { text: '完全不符', score: { comfort: 5, nature: 2 } }] },
  { text: '我喜欢有丰富夜生活的城市，凌晨两点依然能找到吃夜宵的繁华街区。', options: [{ text: '非常符合', score: { social: 5, spicy: 3 } }, { text: '比较符合', score: { social: 4 } }, { text: '一般', score: { social: 3 } }, { text: '不太符合', score: { comfort: 4 } }, { text: '完全不符', score: { comfort: 5, history: 2 } }] },
  { text: '我希望城市有足够多的图书馆、博物馆和高校，让我随时能接触到前沿思潮。', options: [{ text: '非常符合', score: { culture: 5, strive: 3 } }, { text: '比较符合', score: { culture: 4 } }, { text: '一般', score: { culture: 3 } }, { text: '不太符合', score: { social: 4 } }, { text: '完全不符', score: { nature: 5 } }] },
  { text: '我喜欢小城慢生活，邻里相识、物价友好、节奏舒缓，远离内卷。', options: [{ text: '非常符合', score: { comfort: 5, nature: 4 } }, { text: '比较符合', score: { comfort: 4 } }, { text: '一般', score: { comfort: 3 } }, { text: '不太符合', score: { strive: 4 } }, { text: '完全不符', score: { strive: 5, diverse: 3 } }] },
  { text: '我希望定居地有山有海或江河湖泊，周末能随时亲近自然。', options: [{ text: '非常符合', score: { nature: 5 } }, { text: '比较符合', score: { nature: 4 } }, { text: '一般', score: { nature: 3 } }, { text: '不太符合', score: { modern: 4 } }, { text: '完全不符', score: { modern: 5, strive: 3 } }] },
  { text: '我更喜欢口味清淡、讲究食材本味的饮食风格。', options: [{ text: '非常符合', score: { light: 5, culture: 3 } }, { text: '比较符合', score: { light: 4 } }, { text: '一般', score: { light: 3, spicy: 3 } }, { text: '不太符合', score: { spicy: 4 } }, { text: '完全不符', score: { spicy: 5 } }] },
  { text: '我喜欢有市集、庙会、老字号的城市，能感受到浓郁的在地文化气息。', options: [{ text: '非常符合', score: { history: 5, social: 3 } }, { text: '比较符合', score: { history: 4 } }, { text: '一般', score: { history: 3 } }, { text: '不太符合', score: { modern: 4 } }, { text: '完全不符', score: { modern: 5 } }] },
  { text: '我希望居住地创业氛围浓厚，能结识各种行业的有趣灵魂。', options: [{ text: '非常符合', score: { strive: 5, diverse: 4 } }, { text: '比较符合', score: { strive: 4 } }, { text: '一般', score: { strive: 3 } }, { text: '不太符合', score: { comfort: 4 } }, { text: '完全不符', score: { comfort: 5 } }] },
  { text: '我对雾霾、沙尘等污染非常敏感，环境质量是我择城的硬指标。', options: [{ text: '非常符合', score: { nature: 5, south: 3 } }, { text: '比较符合', score: { nature: 4 } }, { text: '一般', score: { nature: 3 } }, { text: '不太符合', score: { strive: 4 } }, { text: '完全不符', score: { strive: 5 } }] },
];

// --- 数据定义区 ---
type QuestionOption = { label?: string; text: string; score: Record<string, number> };
type Question = { id?: number; text: string; options: QuestionOption[] };

const buildQuestions = () => {
  const mbti = MBTI_DATA.questions.map((q) => {
    let scoreA: Record<string, number> = {}, scoreB: Record<string, number> = {};
    const sc = MBTI_DATA.scoring;
    if (sc.EI.E_A.includes(q.id)) { scoreA = { E: 1 }; scoreB = { I: 1 }; } else if (sc.EI.E_B.includes(q.id)) { scoreA = { I: 1 }; scoreB = { E: 1 }; }
    else if (sc.SN.S_A.includes(q.id)) { scoreA = { S: 1 }; scoreB = { N: 1 }; } else if (sc.SN.S_B.includes(q.id)) { scoreA = { N: 1 }; scoreB = { S: 1 }; }
    else if (sc.TF.T_A.includes(q.id)) { scoreA = { T: 1 }; scoreB = { F: 1 }; } else if (sc.TF.T_B.includes(q.id)) { scoreA = { F: 1 }; scoreB = { T: 1 }; }
    else if (sc.JP.J_A.includes(q.id)) { scoreA = { J: 1 }; scoreB = { P: 1 }; } else if (sc.JP.J_B.includes(q.id)) { scoreA = { P: 1 }; scoreB = { J: 1 }; }
    return { text: q.question, options: [{ text: q.optionA, score: scoreA }, { text: q.optionB, score: scoreB }] } as Question;
  });
  const scl90 = SCL90_RAW.map((text, idx) => ({
    text, options: getLikertOptions(['没有', '很轻', '中等', '偏重', '严重'], scl90Map[idx], false, 5),
  })) as Question[];
  const sex_repress = SEX_REPRESS_RAW.map((text) => ({
    text, options: getLikertOptions(['完全不符', '较不符合', '一般', '较符合', '非常符合'], 'repress', true, 5),
  })) as Question[];
  const quirk = QUIRK_RAW.map((q, idx) => {
    const dimKey = QUIRK_DIMENSION_BY_INDEX[idx];
    return {
      text: q.q,
      options: [
        { text: q.a, score: { quirk: 5, [dimKey]: 5 } },
        { text: q.b, score: { quirk: 1, [dimKey]: 1 } },
      ],
    };
  }) as Question[];
  const bipolar = BIPOLAR_RAW.map((text) => ({
    text, options: getLikertOptions(['从无', '轻度', '中度', '偏重', '严重'], 'bipolar', true, 5),
  })) as Question[];
  const dark = [
    qL('只要最终能达成我的重要目标，偶尔跨越道德底线是可以被谅解的。', 'mach'),
    qL('我相信人类本质上都是趋利避害的，无私奉献往往只是一种高级伪装。', 'cynic'),
    qL('我内心深处觉得自己比周围绝大多数人都要聪明、优秀且具有优越感。', 'narc'),
    qL('如果在人际交往中有人恶意冒犯了我，我会记住并寻找机会让他付出代价。', 'malice'),
    qL('我非常擅长察言观色，并能在不知不觉中引导别人的情绪走向我想要的结果。', 'mach2'),
    qL('我极其享受站在权力中心、对他人发号施令并被绝对服从的感觉。', 'power'),
    qL('看到那些平时自以为是的人遭遇挫折或洋相百出，我心里会涌起一阵暗爽。', 'sadism'),
    qL('规章制度和道德条框是用来约束平庸之辈的，真正的强者懂得如何灵活绕过它们。', 'moral'),
    qL('当别人向我哭诉他们的不幸时，我内心其实很难产生真正的同情。', 'psych'),
    qL('在资源有限的情况下，我绝对会优先保障自己的利益，即便牺牲他人。', 'egoism'),
    qL('为了避免麻烦或获取某些便利，我不介意说一些天衣无缝的谎言。', 'mach'),
    qL('我经常在脑海中构建自己获得巨大成功、拥有无尽财富和崇高地位的宏大幻想。', 'narc'),
    qL('面对比我弱势或笨拙的人，我有时会产生想要捉弄、嘲笑甚至欺压他们的冲动。', 'sadism'),
    qL('即使我做的事情伤害到了别人，我往往也能找到借口说服自己，很少感到内疚。', 'psych'),
    qL('如果有必要，我可以毫不犹豫地利用身边的朋友或人脉来作为我成功的跳板。', 'mach2'),
    // 新增题目
    qL('我倾向于认为大多数人都是自私的，只是程度不同而已。', 'cynic'),
    qL('被人仰望、敬畏甚至惧怕，会给我带来满足感。', 'power'),
    qL('为了实现长远目标，隐瞒部分真相或选择性披露信息是必要的策略。', 'mach'),
    qL('我很难理解为什么有人会为陌生人或无关紧要的人感到难过。', 'psych'),
    qL('我认为自己天生就值得比普通人获得更多的尊重和特殊待遇。', 'narc'),
    qL('看到别人在困境中挣扎却无能为力时，我偶尔会感到一丝隐秘的优越。', 'sadism'),
    qL('社会规则是为弱者设立的，有能力的人可以创造自己的规则。', 'moral'),
    qL('在利益冲突中，我很少会为了维持关系而主动退让。', 'egoism'),
    qL('我能精准地摸清每个人的弱点和软肋，并在必要时利用它们。', 'mach2'),
    qL('对于曾经伤害过我的人，我不会轻易忘记，总会在合适的时候\"回报\"。', 'malice'),
    qL('我认为感情用事是成大事者的致命弱点，理性算计才能立于不败之地。', 'mach'),
    qL('当别人夸奖我时，我通常会照单全收，因为我认为他们说的是事实。', 'narc'),
    qL('参与集体活动时，我更在意自己能从中获得什么，而非如何贡献。', 'egoism'),
    qL('当别人因为我的言行而受伤时，我更倾向于反思是否暴露了破绽，而非是否道德。', 'psych'),
    qL('我不介意在背后议论或贬低那些我不喜欢的人，只要不被当事人发现。', 'malice'),
  ] as Question[];
  const talent = [
    qL('我经常能用极具感染力的文字或语言表达复杂的情感，常被夸文笔好或口才佳。', 'lang'),
    qL('面对复杂的逻辑谜题或数据报表，我不仅不头疼，反而能迅速找到破绽。', 'logic'),
    qL('我的脑海里仿佛有一个三维建模引擎，看一眼平面图就能想象出空间细节。', 'space'),
    qL('我习惯定期深度自我剖析，对自己的潜意识动机、优缺点有清醒认知。', 'intro'),
    qL('在复杂的社交场合中，我只需扫一眼就能敏锐捕捉到微妙的气氛变化。', 'inter'),
    qL('无论是学习一支复杂的舞蹈还是精密的机械组装，我的手眼协调能力总超常人。', 'body'),
    qL('我对声音的颗粒度极其敏感，能轻易分辨出乐器中的杂音或判断一首歌走调。', 'music'),
    qL('置身于森林或旷野时我能感受到巨大的能量，天生擅长辨别动植物特征。', 'nature'),
    qL('当所有人都在循规蹈矩时，我的脑子里总能涌现出天马行空的破局方案。', 'create'),
    qL('我对色彩的冷暖搭配、构图的黄金比例有着强迫症般的敏锐度。', 'aesthetic'),
    qL('学习一门全新的外语或者模仿一种方言的口音，对我来说就像呼吸一样自然。', 'lang'),
    qL('我极度享受下棋、编程或玩策略游戏时，大脑推演十几步之后的快感。', 'logic'),
    qL('哪怕去一个完全陌生的大城市，只要走过一遍就能在脑子里生成导航地图。', 'space'),
    qL('在面临重大的人生岔路口时，我极少随波逐流，内心有极为坚定的价值体系。', 'intro'),
    qL('我似乎有一种特殊的魔力，不管多么充满敌意的双方，我都能让他们和平谈判。', 'inter'),
    qL('我能通过精准的词汇和比喻，把抽象概念讲得让人一听就懂。', 'lang'),
    qL('面对一堆杂乱的数据，我总能快速归纳出规律或建立清晰的因果关系。', 'logic'),
    qL('玩密室逃脱或解谜类游戏时，我往往第一个发现空间布局中的机关。', 'space'),
    qL('即使没有外力推动，我也能持续追问「我到底想要什么」并调整人生方向。', 'intro'),
    qL('初次见面就能让对方放松戒备、愿意向我倾诉心事，对我来说并不难。', 'inter'),
    qL('学游泳、学乐器、学手工，我的肌肉记忆和节奏感总比别人来得快。', 'body'),
    qL('听到一段旋律后，我很快就能哼唱或即兴变奏，节奏感极强。', 'music'),
    qL('我能准确说出身边常见植物的名字、习性，对天气和季节变化非常敏感。', 'nature'),
    qL('别人眼中的「异想天开」，在我这里往往是可行的创新起点。', 'create'),
    qL('装修房间、搭配衣服或设计PPT时，我对平衡与美感有近乎本能的追求。', 'aesthetic'),
  ] as Question[];
  const needs = [
    qL('我投身于一项事业的绝对前提，是它必须拥有深刻的社会价值和信仰意义。', 'meaning'),
    qL('我一生的终极追求，就是找到那份能够将我完全托底、百分百无条件包容的爱。', 'love'),
    qL('泛泛之交只会让我感到疲惫，我极度渴望能直击灵魂、彼此完全剖析的共鸣。', 'connect'),
    qL('停滞不前是对我最大的精神折磨，我必须感觉到自己每天都在疯狂吸收知识。', 'growth'),
    qL('我来到这个世界绝不是为了顺应它，我必须留下颠覆性的创造或属于我的作品。', 'create'),
    qL('相比于作为一颗螺丝钉被安排，我有着极其强烈的野心去掌控全局、制定规则。', 'power'),
    qL('无聊和一眼望到头的生活简直就是坟墓，我的人生必须是由极致的刺激与快乐组成。', 'fun'),
    qL('失控感会让我极度焦虑，我需要卡里有足够的钱、生活有绝对的确切感才能安心。', 'safety'),
    qL('任何人试图用规矩、道德或关系来捆绑我，我都会产生极其强烈的反叛心理。', 'freedom'),
    qL('只要看到别人因为我的付出而变得更美好，这种成就感胜过任何金钱的回报。', 'contribute'),
    qL('如果一份工作让我觉得只是在制造无意义的工业垃圾，哪怕薪水再高也做不下去。', 'meaning'),
    qL('如果在亲密关系中感受不到对方持续的、热烈的偏爱，我的内心就会迅速枯萎。', 'love'),
    qL('我不害怕孤独，但我极度害怕置身于人群中却找不到一个能接住我复杂情绪的同类。', 'connect'),
    qL('我极度享受走出舒适区、被艰难挑战逼到极限，然后破茧重生的那种撕裂的快感。', 'growth'),
    qL('从零到一的拓荒者才是我向往的角色，跟在别人屁股后面模仿对我来说是耻辱。', 'create'),
    qL('我宁愿放弃眼前的安稳，也要去追求那个能定义我人生意义的使命。', 'meaning'),
    qL('被爱、被理解、被接纳，对我来说比事业成功和财富积累更重要。', 'love'),
    qL('我渴望加入一个能彼此深度信任、共同成长的圈子，而不是孤军奋战。', 'connect'),
    qL('重复做同样的事会让我发疯，我必须不断学习新技能、接触新领域。', 'growth'),
    qL('我无法接受庸庸碌碌过完一生，总想做出点能让人记住的事。', 'create'),
    qL('在团队或家庭中，我希望自己是有话语权、能做决定的那个人。', 'power'),
    qL('旅行、聚会、冒险、尝试新鲜事物，这些才是我活着的理由。', 'fun'),
    qL('我需要在买房、存款、保险等方面有明确保障，才能放心追求梦想。', 'safety'),
    qL('我最反感别人替我拿主意，哪怕是出于好意，我也要自己说了算。', 'freedom'),
    qL('帮助他人成长、解决他们的困境，能给我带来深层次的满足感。', 'contribute'),
  ] as Question[];
  const role = [
    qL('在亲密互动中，我有一种强烈的欲望去主导整个过程，包括节奏、姿势和对方的反应。', 'dom'),
    qL('我享受被彻底压制的感觉，放弃思考，将身体和意志的决定权完全交托给伴侣。', 'sub'),
    qL('我并没有固定的控制欲或臣服欲，这完全取决于我面对的伴侣是谁，我能在两者间自如切换。', 'switch'),
    qL('我认为亲密关系必须建立在绝对平等、温柔和互相尊重的基础上，排斥任何形式的权力上下位。', 'vanilla'),
    qL('看到伴侣因为我的指令而露出顺从、甚至带点羞怯或痛苦的表情，会让我极度兴奋。', 'sadism'),
    qL('适度的身体约束（如被绑住双手）、被蒙上眼睛或被命令，能让我获得极大的安全感与快感。', 'masochism'),
    qL('我喜欢故意违抗伴侣的指令、顶嘴调皮，然后享受对方失去耐心并强势制服我的过程。', 'brat'),
    qL('在失控的边缘，像野兽一样啃咬、留下痕迹、抛弃人类的理智，能带给我最强烈的快感。', 'primal'),
    qL('在安全的约定下，体验轻微的痛觉或被伴侣像对待"物品"一样对待，能让我从日常压力中彻底解脱。', 'masochism'),
    qL('我对BDSM文化充满好奇且没有包袱，无论是拿着鞭子还是戴上项圈，只要氛围对了我都乐意尝试。', 'switch'),
    qL('在生活中我可能很温和，但在私密空间里，我骨子里有着极强的侵略性和征服欲等待被释放。', 'dom'),
    qL('哪怕我在职场上是个雷厉风行的领导者，但在伴侣面前，我最渴望的是卸下伪装被对方完全支配。', 'sub'),
    qL('绵长细腻的前戏、充满爱意的对视和事后的温存拥抱，对我来说远比追求花里胡哨的刺激更重要。', 'vanilla'),
    qL('太温顺的互动让我觉得无聊，我需要一点火药味和互相挑衅的张力。', 'brat'),
    qL('像野兽一样撕咬、低吼、抛弃人类的文明理智，能带给我最原始的快感。', 'primal'),
    qL('发号施令、制定规则、让对方按我的意愿行事，会让我感到强烈的成就感与掌控感。', 'dom'),
    qL('被需要、被依赖、被对方全身心托付的感觉，能给我深层次的情感满足。', 'sub'),
    qL('我倾向于根据对方的性格和状态，灵活选择是主导还是顺从，而非固定一种模式。', 'switch'),
    qL('我不需要权力游戏或刺激项目，平淡真挚的情感交流就足以让我感到幸福。', 'vanilla'),
    qL('看到对方因我而羞耻、紧张或不知所措，会激发我内心的满足与愉悦。', 'sadism'),
    qL('被束缚、被控制、在对方面前完全卸下防备，会让我感到前所未有的放松与信任。', 'masochism'),
    qL('故意惹恼对方、挑起小摩擦再被\"收拾\"，这种博弈过程让我乐此不疲。', 'brat'),
    qL('在亲密时刻，我渴望卸下所有社会面具，释放最原始、最本能的冲动。', 'primal'),
  ] as Question[];
  const preference = [
    qL('我希望伴侣把我放在最优先的位置，不接受模糊边界。', 'possession'),
    qL('伴侣和他人过于亲近会让我明显不舒服。', 'possession'),
    qL('我更向往稳定、长期、专一的亲密关系。', 'pure_love'),
    qL('我把情感安全感看得比刺激感更重要。', 'pure_love'),
    qL('没有情绪冲突的互动会让我觉得不够有张力。', 'abuse'),
    qL('我会被强势、压制感较强的互动氛围吸引。', 'abuse'),
    qL('出现第三者竞争时，我的情绪反应会更强。', 'ntr'),
    qL('关系里有“比较与争夺”元素会放大我的关注度。', 'ntr'),
    qL('“观察/被观察”的情境会提升我的兴奋度。', 'voyeur_guard'),
    qL('我对窥探感、展示感相关情境有持续好奇。', 'voyeur_guard'),
    qL('我不喜欢被规定太多，越限制越想反着来。', 'rebel'),
    qL('在亲密关系里，我会故意试探规则底线。', 'rebel'),
    qL('在安全共识下，我接受多样化的亲密方式。', 'open'),
    qL('只要边界清楚，我对非传统表达持开放态度。', 'open'),
    qL('我会幻想被更强势的一方带节奏。', 'forced'),
    qL('失去部分主导权反而会让我更投入。', 'forced'),
    qL('我更享受自己主导流程和节奏。', 'coercive'),
    qL('在互动中，我偏好明确发起和推进。', 'coercive'),
    qL('我愿意探索更广泛的性别表达与角色。', 'trans'),
    qL('我对跨性别或流动性别议题有较高接受度。', 'trans'),
    qL('特定穿搭、材质或道具会明显影响我的偏好。', 'fetish'),
    qL('我对“物件触发型”刺激有较稳定偏好。', 'fetish'),
    qL('我喜欢通过设定角色来增强投入感。', 'roleplay'),
    qL('有剧情、有身份设定的互动更容易让我进入状态。', 'roleplay'),
    qL('在共识前提下，被否定感会带来复杂刺激。', 'humiliated'),
    qL('我可以接受“被弱化/被压低位置”的互动体验。', 'humiliated'),
    qL('语言上的压制会提高我的情绪反应。', 'humiliation'),
    qL('我对“被命令/被训斥”类情境有一定偏好。', 'humiliation'),
    qL('我对同性群体相关情境有较高兴趣。', 'same_sex_group'),
    qL('同性多方互动设定会提升我的关注度。', 'same_sex_group'),
    qL('我对公开性或展示性的情境有好奇。', 'exposure'),
    qL('适度“被看到”的风险感会提高刺激强度。', 'exposure'),
    qL('在关系里，我更容易进入顺从与跟随状态。', 'submission'),
    qL('被明确引导会让我更有安全感。', 'submission'),
    qL('在关系里，我更偏好征服感与掌控感。', 'conquest'),
    qL('我享受把节奏牢牢握在自己手里的感觉。', 'conquest'),
    qL('我偏好“指令-反馈”非常清晰的互动结构。', 'obedience_control'),
    qL('我喜欢可执行、可验证的控制与服从规则。', 'obedience_control'),
    qL('在安全边界内，我对痛觉和压迫感有好奇。', 'masochism'),
    qL('轻微不适感反而可能让我更专注当下。', 'masochism'),
    qL('我更偏好关系中的排他承诺，而非多人边界。', 'pure_love'),
    qL('我不喜欢伴侣把情感和注意力分散给太多人。', 'possession'),
  ] as Question[];
  const abo = [
    qL('在任何团队或群体中，我总是会不自觉地掌控话语权，并自然而然地成为那个做出最终决策的领袖。', 'alpha'),
    qL('我极其讨厌冲突，在人际交往中我总是扮演那个调和矛盾、维持稳定与平衡的"和事佬"角色。', 'beta'),
    qL('我天生对情绪的变化有着极其敏锐的感知力，别人的痛苦或喜悦很容易直接穿透我并让我产生共情。', 'omega'),
    qL('我对属于我的领地、资源和亲近的人有着极其强烈的占有欲和保护欲，任何人的觊觎都会激怒我。', 'alpha'),
    qL('我不喜欢出风头，比起成为站在聚光灯下承担巨大压力的焦点，我更愿意做一个默默提供支持的可靠后盾。', 'beta'),
    qL('在亲密关系中，我非常容易产生依赖感，极度渴望被一个强大、坚定且充满力量的伴侣所庇护和包容。', 'omega'),
    qL('我骨子里有着极强的攻击性和竞争欲，面对挑战我绝不退缩，享受战胜对手、将其踩在脚下的快感。', 'alpha'),
    qL('我的情绪状态非常稳定，极少大喜大悲，身边的朋友都觉得我是一个可以随时依靠、理性且务实的人。', 'beta'),
    qL('当周围环境充满火药味或敌意时，我会感到极度的不安和恐惧，想要迅速逃离或寻找一个安全的避风港。', 'omega'),
    qL('我习惯用命令的语气说话，当别人没有立即执行我的意图或试图反驳我时，我会感到明显的不耐烦。', 'alpha'),
    qL('我觉得这个世界上没有什么是绝对非黑即白的，比起极端的站队，我更倾向于寻找折中和双赢的解决方案。', 'beta'),
    qL('我有着非常强烈的奉献精神和付出欲，照顾别人、倾听别人的烦恼并给予情感抚慰，是我自我价值的体现。', 'omega'),
  ] as Question[];
  const city = CITY_QUESTIONS as Question[];
  const sbti = SBTI_QUESTIONS.map((q) => ({
    id: q.id,
    text: q.text,
    options: q.options.map((opt) => ({
      label: opt.label,
      text: opt.text,
      score: q.dimension ? { [q.dimension]: opt.score } : {},
    })),
  })) as Question[];

  return { mbti, scl90, sex_repress, quirk, bipolar, dark, talent, needs, role, preference, abo, city, sbti };
};

const QUESTIONS = buildQuestions();

const TEST_META: { id: string; title: string; desc: string; time: string; count: number; color: string }[] = [
  { id: 'sbti', title: 'SBTI', desc: '来源于 UnluckyNinja/SBTI-test 的完整镜像题库，覆盖 15 维度与 27 人格类型。', time: '8-12分钟', count: 31, color: 'bg-violet-50 text-violet-600' },
  { id: 'mbti', title: 'MBTI人格类型测试', desc: '麦尔斯-碧瑞斯人格类型量表（M版本93题），全面测算你与生俱来的自然偏好与性格模型。', time: '10-15分钟', count: QUESTIONS.mbti.length, color: 'bg-amber-50 text-amber-600' },
  { id: 'mbti_stage', title: 'MBTI阶段', desc: '32题测出你的MBTI人格类型与心智阶位，输出雷达图、进化行动与匹配建议。', time: '6-8分钟', count: 32, color: 'bg-emerald-50 text-emerald-600' },
  { id: 'beauty', title: '颜值测试', desc: '上传照片并选择男生/女生，生成五官细节级夸赞分析报告。', time: '1-2分钟', count: 1, color: 'bg-rose-50 text-rose-600' },
  { id: 'scl90', title: '抑郁症量表', desc: '经典90题心理健康筛查，涵盖躯体化、强迫、抑郁等9大核心因子分析。', time: '10-12分钟', count: QUESTIONS.scl90.length, color: 'bg-teal-50 text-teal-600' },
  { id: 'bipolar', title: '双相情感障碍筛查', desc: '科学筛查情绪的高涨与低落波动，测算躁郁症（双相）潜伏风险指数。', time: '5分钟', count: QUESTIONS.bipolar.length, color: 'bg-cyan-50 text-cyan-600' },
  { id: 'sex_repress', title: '性压抑指数测试', desc: '深入潜意识，评估你在亲密关系、欲望表达上的心理包袱与羞耻感维度。', time: '5分钟', count: QUESTIONS.sex_repress.length, color: 'bg-pink-50 text-pink-600' },
  { id: 'quirk', title: '心理怪癖测试', desc: '覆盖12项微行为指标，定位你的强迫、洁癖、控制与焦虑型习惯。', time: '4分钟', count: QUESTIONS.quirk.length, color: 'bg-yellow-50 text-yellow-600' },
  { id: 'preference', title: '性偏好测试', desc: '基于20项偏好维度，输出你的亲密互动倾向雷达与关键词画像。', time: '8-10分钟', count: QUESTIONS.preference.length, color: 'bg-cyan-50 text-cyan-600' },
  { id: 'role', title: '性角色倾向', desc: '深层剖析亲密关系中的权力偏好（Dom/Sub/Brat/Primal等），探索真实欲望。', time: '5分钟', count: QUESTIONS.role.length, color: 'bg-rose-50 text-rose-600' },
  { id: 'abo', title: '潜在性别测试', desc: '抛开生理性别，从潜意识社会角色分工测算你灵魂深处的Alpha/Beta/Omega属性。', time: '5分钟', count: QUESTIONS.abo.length, color: 'bg-indigo-50 text-indigo-600' },
  { id: 'dark', title: '黑暗人格', desc: '经典SD3量表升级版，直击人性幽暗面，探索你的马基亚维利与权谋倾向。', time: '10分钟', count: QUESTIONS.dark.length, color: 'bg-slate-50 text-slate-700' },
  { id: 'talent', title: '天赋能力图', desc: '深度对标加德纳多元智能理论，全面扫瞄你的8大核心智能与隐藏天赋。', time: '6-7分钟', count: QUESTIONS.talent.length, color: 'bg-purple-50 text-purple-600' },
  { id: 'needs', title: '精神需求测试', desc: '剖析马斯洛需求与自我决定理论，定位你底层行为的核心驱动力。', time: '6-7分钟', count: QUESTIONS.needs.length, color: 'bg-green-50 text-green-600' },
  { id: 'city', title: '最适合你的城市', desc: '基于多维度的深度测算，精准匹配最契合你灵魂和发展偏好的理想之城。', time: '5-6分钟', count: QUESTIONS.city.length, color: 'bg-blue-50 text-blue-600' },
  { id: 'enneagram', title: '九型人格测试', desc: '经典九型人格量表，探索你的核心动机与行为模式。', time: '10-12分钟', count: 54, color: 'bg-cyan-50 text-cyan-600' },
];

const TOPBAR_SHORT_TITLE: Record<string, string> = {
  mbti: 'MBTI',
  mbti_stage: 'MBTI阶段',
  beauty: '颜值',
  scl90: '抑郁症',
  bipolar: '双相筛查',
  sex_repress: '性压抑',
  quirk: '心理怪癖',
  preference: '性偏好',
  role: '性角色',
  abo: '潜在性别',
  talent: '天赋图',
  needs: '精神需求',
  city: '理想城市',
  enneagram: '九型人格',
  sbti: 'SBTI',
};

const TEST_INTRO_BENEFITS: Record<string, string> = {
  mbti: '完成后你会得到完整人格类型、四维度偏好比例、行为优势与沟通方式建议，适合用于职业选择、关系协作和自我管理。',
  mbti_stage: '除了基础 MBTI 类型，还会给出当前心智阶位、阶段性卡点和可执行进化动作，适合做阶段复盘与成长规划。',
  beauty: '上传一张清晰照片即可生成结构化颜值报告，包含五官评分、风格建议、妆容方向和提升方案，帮助你快速找到最适合自己的路线。',
  scl90: '用于快速筛查近期心理状态，帮助你识别焦虑、抑郁、强迫等维度变化，建立更清晰的自我观察基线。',
  bipolar: '用于识别情绪高涨与低落波动趋势，帮助你判断是否需要更早进行生活节律调整与专业评估。',
  sex_repress: '用于评估你在亲密表达中的压抑与羞耻模式，帮助你更清晰理解边界、需求与关系中的心理反应。',
  quirk: '通过12个行为维度（如对称强迫、反复检查、洁净边界、数字执念等）定位你的日常怪癖来源，报告会给出雷达图、前三高维度和可执行的减压建议，适合快速自查“我为什么总在这些细节上卡住”。',
  preference: '通过20项亲密偏好指标（如占有、纯爱、开放、扮演、臣服、征服等）绘制你的偏好雷达图，帮助你识别“真正喜欢什么、边界在哪里、如何沟通更安全”。',
  role: '用于探索亲密关系中的权力与互动偏好，让你更明确自己在关系中的舒适区、触发点与沟通策略。',
  abo: '用于从社会互动偏好角度观察你的角色倾向，帮助你在团队协作和关系定位中更快找到适配位置。',
  dark: '用于识别人际策略里的控制、利己与防御倾向，帮助你看清高压场景下的决策风格与风险点。',
  talent: '用于扫描多维智能结构与优势天赋，帮助你找到最容易形成长期竞争力的能力组合。',
  needs: '用于定位你的核心心理驱动力，帮助你解释“为什么总在同类问题上反复”，并给出更稳的行动优先级。',
  city: '用于匹配更适合你发展的城市画像，从节奏、资源、气候与生活偏好维度给出方向参考。',
  enneagram: '用于识别你的核心动机、防御模式和关系应对方式，帮助你在压力、亲密关系和成长决策中更有方向感。',
  sbti: 'SBTI 是一个偏年轻化的人格测试，通过多维问题评估你的行为与心理倾向，输出主人格类型、性格说明和 15 维度画像，适合做自我认知与社交分享。',
};

const copyTextToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // ignore and fallback
  }
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    textarea.style.pointerEvents = 'none';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
};

const shareSbtiLink = async (mode: 'entry' | 'result' = 'entry') => {
  const shareUrl = mode === 'result' ? `${SBTI_SHARE_URL}&scene=result` : SBTI_SHARE_URL;
  const sharePayload = mode === 'result'
    ? { title: '我的 SBTI 测试结果', url: shareUrl }
    : { url: shareUrl };
  try {
    if (navigator.share) {
      await navigator.share(sharePayload);
      return;
    }
  } catch {
    // user canceled or device blocked native share, fallback to clipboard
  }
  const copied = await copyTextToClipboard(shareUrl);
  if (copied) {
    alert(mode === 'result'
      ? 'SBTI 结果页链接已复制，可直接粘贴给微信好友。'
      : 'SBTI 测试链接已复制，可直接粘贴给微信好友。');
    return;
  }
  alert('当前设备暂不支持系统分享，且复制失败，请稍后重试。');
};

type ScoreRecord = Record<string, { total: number; count: number }>;
type PsychHistoryRecord = {
  id: string;
  testId: string;
  createdAt: number;
  summary: string;
  scores: ScoreRecord;
};

const PSY_TEST_HISTORY_KEY = 'psych_test_history_v1';
const MAX_HISTORY_PER_TEST = 10;

const getAvg = (scores: ScoreRecord, key: string) => {
  if (!scores[key] || scores[key].count === 0) return 0;
  return scores[key].total / scores[key].count;
};
const getScorePct = (scores: ScoreRecord, key: string, max = 5) => {
  const avg = getAvg(scores, key);
  return Math.max(0, Math.min(100, Math.round(((avg - 1) / (max - 1)) * 100)));
};

const getPreferenceScoreMap = (scores: ScoreRecord): Record<string, number> => {
  const out: Record<string, number> = {};
  PREFERENCE_TRAITS.forEach((item) => {
    // 每个指标分数直接由该维度答题均值换算，确保与用户答案一一对应。
    out[item.key] = getScorePct(scores, item.key, 5);
  });
  return out;
};

const formatHistoryTime = (ts: number): string => {
  if (!Number.isFinite(ts) || ts <= 0) return '--';
  return new Date(ts).toLocaleString('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const loadPsychHistory = (): PsychHistoryRecord[] => {
  try {
    const raw = localStorage.getItem(PSY_TEST_HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((item) => item && typeof item === 'object' && typeof item.testId === 'string')
      .map((item) => ({
        id: String(item.id || ''),
        testId: String(item.testId || ''),
        createdAt: Number(item.createdAt || 0),
        summary: String(item.summary || ''),
        scores: (item.scores && typeof item.scores === 'object') ? item.scores as ScoreRecord : {},
      }))
      .filter((item) => item.id && item.testId);
  } catch {
    return [];
  }
};

const getHistorySummary = (testId: string, scores: ScoreRecord): string => {
  if (testId === 'mbti') {
    const getSum = (key: string) => (scores[key] ? scores[key].total : 0);
    const E = getSum('E'), I = getSum('I'), S = getSum('S'), N = getSum('N'), T = getSum('T'), F = getSum('F'), J = getSum('J'), P = getSum('P');
    return `${E >= I ? 'E' : 'I'}${S >= N ? 'S' : 'N'}${T >= F ? 'T' : 'F'}${J >= P ? 'J' : 'P'} 人格画像`;
  }
  if (testId === 'scl90') {
    let total = 0;
    let count = 0;
    Object.keys(scores).forEach((k) => {
      total += scores[k].total;
      count += scores[k].count;
    });
    return `全局指数 ${(count > 0 ? total / count : 1).toFixed(2)}`;
  }
  if (testId === 'bipolar') return `波动指数 ${getScorePct(scores, 'bipolar')}%`;
  if (testId === 'sex_repress') return `压抑指数 ${getScorePct(scores, 'repress')}%`;
  if (testId === 'quirk') return `怪癖指数 ${getScorePct(scores, 'quirk')}%`;
  if (testId === 'preference') {
    const prefMap = getPreferenceScoreMap(scores);
    const top = [...PREFERENCE_TRAITS]
      .map((item) => ({ label: item.label, val: Number(prefMap[item.key] || 0) }))
      .sort((a, b) => b.val - a.val)[0];
    return `偏好核心 ${top?.label || '待解析'}`;
  }
  if (testId === 'dark') {
    const keys = ['egoism', 'mach', 'mach2', 'narc', 'power', 'psych', 'sadism', 'moral', 'malice', 'cynic'];
    const avg = Math.round(keys.reduce((sum, key) => sum + getScorePct(scores, key), 0) / keys.length);
    return `暗黑指数 ${avg}%`;
  }
  if (testId === 'role') {
    const options: Array<[string, number]> = [
      ['Dom', getScorePct(scores, 'dom')],
      ['Sub', getScorePct(scores, 'sub')],
      ['Switch', getScorePct(scores, 'switch')],
      ['Vanilla', getScorePct(scores, 'vanilla')],
    ];
    options.sort((a, b) => b[1] - a[1]);
    return `核心倾向 ${options[0]?.[0] || '待解析'}`;
  }
  if (testId === 'abo') {
    const options: Array<[string, number]> = [
      ['Alpha', getScorePct(scores, 'alpha')],
      ['Beta', getScorePct(scores, 'beta')],
      ['Omega', getScorePct(scores, 'omega')],
    ];
    options.sort((a, b) => b[1] - a[1]);
    return `灵魂属性 ${options[0]?.[0] || '待解析'}`;
  }
  if (testId === 'city') return '城市匹配结果';
  if (testId === 'talent') return '天赋能力图谱';
  if (testId === 'needs') return '精神需求图谱';
  return '心理测试报告';
};

interface PsychologicalTestsProps {
  onBack: () => void;
  onRequireLogin?: () => void;
  /** 直接打开指定测试，跳过首页列表 */
  initialTestId?: string;
}

function mergeScoresInto(base: ScoreRecord, scoreObj: Record<string, number>, add: boolean): ScoreRecord {
  const next = { ...base };
  for (const [key, val] of Object.entries(scoreObj)) {
    if (!next[key]) next[key] = { total: 0, count: 0 };
    next[key] = {
      total: next[key]!.total + (add ? val : -val),
      count: next[key]!.count + (add ? 1 : -1),
    };
  }
  return next;
}

export default function PsychologicalTests({ onBack, onRequireLogin, initialTestId }: PsychologicalTestsProps) {
  const [view, setView] = useState<'home' | 'enneagram' | 'mbti_stage' | 'beauty' | 'intro' | 'quiz' | 'result'>(() => {
    if (!initialTestId) return 'home';
    if (initialTestId === 'enneagram') return 'intro';
    if (initialTestId === 'mbti_stage') return 'mbti_stage';
    if (initialTestId === 'beauty') return 'beauty';
    return 'intro';
  });
  const [activeTest, setActiveTest] = useState<string | null>(() => initialTestId || null);
  const [enneagramPreviewMode, setEnneagramPreviewMode] = useState(false);
  const [answeredScores, setAnsweredScores] = useState<Array<Record<string, number>>>([]);
  const [scores, setScores] = useState<ScoreRecord>({});
  const [sbtiFlow, setSbtiFlow] = useState<SbtiQuestion[]>([]);
  const [sbtiAnswers, setSbtiAnswers] = useState<Array<{ questionId: number; label: string }>>([]);
  const [sbtiResult, setSbtiResult] = useState<SbtiResult | null>(null);
  const [historyRecords, setHistoryRecords] = useState<PsychHistoryRecord[]>([]);
  const [psychActivationOpen, setPsychActivationOpen] = useState(false);
  const [psychActivationCode, setPsychActivationCode] = useState('');
  const [psychActivationLoading, setPsychActivationLoading] = useState(false);
  const [psychActivationMsg, setPsychActivationMsg] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    if (!document.getElementById('html2canvas-script')) {
      const script = document.createElement('script');
      script.id = 'html2canvas-script';
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
      document.head.appendChild(script);
    }
  }, []);

  useEffect(() => {
    setHistoryRecords(loadPsychHistory());
  }, []);

  const mapSbtiQuestion = (q: SbtiQuestion): Question => ({
    id: q.id,
    text: q.text,
    options: q.options.map((opt) => ({
      label: opt.label,
      text: opt.text,
      score: q.dimension ? { [q.dimension]: opt.score } : {},
    })),
  });

  const getResolvedSbtiFlow = (
    baseFlow: SbtiQuestion[],
    answers: Array<{ questionId: number; label: string }>,
  ): SbtiQuestion[] => {
    const answerMap: Record<number, string> = {};
    answers.forEach((a) => {
      answerMap[a.questionId] = a.label;
    });
    return withDrinkTriggerIfNeeded(baseFlow, answerMap);
  };

  const getActiveQuestions = (
    targetTest: string | null,
    targetSbtiAnswers: Array<{ questionId: number; label: string }> = sbtiAnswers,
    targetSbtiFlow: SbtiQuestion[] = sbtiFlow,
  ): Question[] => {
    if (!targetTest) return [];
    if (targetTest !== 'sbti') {
      return QUESTIONS[targetTest as keyof typeof QUESTIONS] || [];
    }
    const baseFlow = targetSbtiFlow.length > 0 ? targetSbtiFlow : createSbtiQuestionFlow();
    const resolved = getResolvedSbtiFlow(baseFlow, targetSbtiAnswers);
    return resolved.map(mapSbtiQuestion);
  };

  const ensurePsychResultAccess = async (testId: string): Promise<boolean> => {
    if (testId === 'sbti') return true;
    const user = getStoredUser();
    if (isProMember(user)) return true;
    const userId = String(user?.userId || '').trim();
    if (!userId) {
      setPsychActivationMsg({ ok: false, text: '请先登录后查看心理测试报告。' });
      if (onRequireLogin) onRequireLogin();
      return false;
    }
    const gate = await checkFeatureTrialAccess({
      userId,
      feature: testId,
      groupKey: PSY_TRIAL_GROUP_KEY,
      consume: true,
    });
    if (gate?.allowed) return true;
    setPsychActivationCode('');
    setPsychActivationMsg({ ok: false, text: '你已完成一次免费测试。第二个测试的结果需要激活码解锁。' });
    setPsychActivationOpen(true);
    return false;
  };

  const ensurePsychResultAccessWithPending = async (testId: string): Promise<boolean> => {
    const allowed = await ensurePsychResultAccess(testId);
    return allowed;
  };

  const saveHistoryRecord = (testId: string, nextScores: ScoreRecord) => {
    const record: PsychHistoryRecord = {
      id: `${testId}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      testId,
      createdAt: Date.now(),
      summary: getHistorySummary(testId, nextScores),
      scores: nextScores,
    };
    setHistoryRecords((prev) => {
      const merged = [record, ...prev];
      const perTestCount: Record<string, number> = {};
      const trimmed = merged.filter((item) => {
        const n = (perTestCount[item.testId] || 0) + 1;
        perTestCount[item.testId] = n;
        return n <= MAX_HISTORY_PER_TEST;
      });
      localStorage.setItem(PSY_TEST_HISTORY_KEY, JSON.stringify(trimmed));
      return trimmed;
    });
  };

  const selectTest = (testId: string) => {
    setEnneagramPreviewMode(false);
    setSbtiFlow([]);
    setSbtiAnswers([]);
    setSbtiResult(null);
    if (testId === 'enneagram') {
      setActiveTest('enneagram');
      setView('intro');
      return;
    }
    if (testId === 'mbti_stage') {
      setActiveTest('mbti_stage');
      setView('mbti_stage');
      return;
    }
    if (testId === 'beauty') {
      setActiveTest('beauty');
      setView('beauty');
      return;
    }
    setActiveTest(testId);
    setView('intro');
  };

  const startTest = () => {
    setEnneagramPreviewMode(false);
    setAnsweredScores([]);
    setScores({});
    setSbtiAnswers([]);
    setSbtiResult(null);
    setSbtiFlow([]);
    if (activeTest === 'enneagram') {
      setView('enneagram');
      return;
    }
    if (activeTest === 'sbti') {
      setSbtiFlow(createSbtiQuestionFlow());
      setView('quiz');
      return;
    }
    setView('quiz');
  };

  const handleViewSampleReport = () => {
    if (activeTest === 'enneagram') {
      setEnneagramPreviewMode(true);
      setView('enneagram');
      return;
    }
    if (activeTest === 'sbti') {
      const sampleFlow = createSbtiQuestionFlow(() => 0.42);
      const sampleResolved = getResolvedSbtiFlow(sampleFlow, []);
      const sampleAnswers: Array<{ questionId: number; label: string }> = [];
      sampleResolved.forEach((q) => {
        if (q.id === SBTI_DRINK_GATE_ID) {
          sampleAnswers.push({ questionId: q.id, label: 'B' });
          return;
        }
        if (q.id === SBTI_DRINK_TRIGGER_ID) return;
        sampleAnswers.push({ questionId: q.id, label: 'B' });
      });
      const result = computeSbtiResult(sampleAnswers);
      if (!result) return;
      setSbtiFlow(sampleFlow);
      setSbtiAnswers(sampleAnswers);
      setSbtiResult(result);
      const targetTestId = 'sbti';
      void (async () => {
        if (await ensurePsychResultAccessWithPending(targetTestId)) {
          setView('result');
        }
      })();
      return;
    }
    const qs = QUESTIONS[activeTest as keyof typeof QUESTIONS];
    if (!qs) return;
    const newScores: ScoreRecord = {};
    qs.forEach((q) => {
      const randomOption = q.options[Math.floor(Math.random() * q.options.length)];
      Object.entries(randomOption.score).forEach(([t, v]) => {
        if (!newScores[t]) newScores[t] = { total: 0, count: 0 };
        newScores[t].total += v;
        newScores[t].count += 1;
      });
    });
    setScores(newScores);
    if (activeTest) saveHistoryRecord(activeTest, newScores);
    const targetTestId = String(activeTest || '').trim();
    void (async () => {
      if (!targetTestId) return;
      if (await ensurePsychResultAccessWithPending(targetTestId)) {
        setView('result');
        return;
      }
    })();
  };

  const handleAnswer = (answer: { score: Record<string, number>; questionId?: number; label?: string }) => {
    const scoreObj = answer.score;
    const nextAnswered = [...answeredScores, scoreObj];
    setAnsweredScores(nextAnswered);
    const newScores = nextAnswered.reduce((acc, s) => mergeScoresInto(acc, s, true), {} as ScoreRecord);
    setScores(newScores);

    let nextSbtiAnswers = sbtiAnswers;
    let nextSbtiFlow = sbtiFlow;
    if (activeTest === 'sbti' && answer.questionId && answer.label) {
      nextSbtiAnswers = [...sbtiAnswers, { questionId: answer.questionId, label: answer.label }];
      const baseFlow = sbtiFlow.length > 0 ? sbtiFlow : createSbtiQuestionFlow();
      nextSbtiFlow = getResolvedSbtiFlow(baseFlow, nextSbtiAnswers);
      setSbtiAnswers(nextSbtiAnswers);
      setSbtiFlow(nextSbtiFlow);
    }

    const qs = getActiveQuestions(activeTest, nextSbtiAnswers, nextSbtiFlow);
    if (qs && nextAnswered.length >= qs.length) {
      if (activeTest === 'sbti') {
        let result = computeSbtiResult(nextSbtiAnswers);
        if (!result) {
          const answerMap = new Map<number, string>();
          nextSbtiAnswers.forEach((item) => answerMap.set(item.questionId, item.label));
          const repairedAnswers = [...nextSbtiAnswers];
          SBTI_QUESTIONS.forEach((q) => {
            if (q.isSpecial) return;
            if (!answerMap.has(q.id)) {
              repairedAnswers.push({ questionId: q.id, label: 'B' });
            }
          });
          result = computeSbtiResult(repairedAnswers);
          if (result) {
            nextSbtiAnswers = repairedAnswers;
            setSbtiAnswers(repairedAnswers);
          }
        }
        setSbtiResult(result || null);
      } else if (activeTest) {
        saveHistoryRecord(activeTest, newScores);
      }
      const targetTestId = String(activeTest || '').trim();
      void (async () => {
        if (!targetTestId) return;
        if (await ensurePsychResultAccessWithPending(targetTestId)) {
          setView('result');
          return;
        }
        setView('intro');
      })();
    }
  };

  const handlePrevQuestion = () => {
    if (answeredScores.length === 0) return;
    const last = answeredScores[answeredScores.length - 1]!;
    const nextAnswered = answeredScores.slice(0, -1);
    setAnsweredScores(nextAnswered);
    setScores((prev) => mergeScoresInto(prev, last, false));
    if (activeTest === 'sbti') {
      const nextSbtiAnswers = sbtiAnswers.slice(0, -1);
      const baseFlow = sbtiFlow.length > 0 ? sbtiFlow : createSbtiQuestionFlow();
      const nextFlow = getResolvedSbtiFlow(baseFlow, nextSbtiAnswers);
      setSbtiAnswers(nextSbtiAnswers);
      setSbtiFlow(nextFlow);
      setSbtiResult(null);
    }
  };

  const resetHome = () => {
    setEnneagramPreviewMode(false);
    setSbtiFlow([]);
    setSbtiAnswers([]);
    setSbtiResult(null);
    setView('home');
    setActiveTest(null);
  };

  const goBackFromEnneagram = () => {
    setEnneagramPreviewMode(false);
    if (initialTestId) onBack();
    else { setView('home'); setActiveTest(null); }
  };

  const goBackFromStandaloneTest = () => {
    if (initialTestId) onBack();
    else { setView('home'); setActiveTest(null); }
  };

  const activeHistoryRecords = activeTest
    ? (activeTest === 'sbti'
      ? []
      : historyRecords.filter((item) => item.testId === activeTest).slice(0, MAX_HISTORY_PER_TEST))
    : [];

  const openHistoryRecord = (record: PsychHistoryRecord) => {
    setActiveTest(record.testId);
    setAnsweredScores([]);
    setScores(record.scores);
    const targetTestId = String(record.testId || '').trim();
    void (async () => {
      if (!targetTestId) return;
      if (await ensurePsychResultAccessWithPending(targetTestId)) {
        setView('result');
        return;
      }
    })();
  };

  const copyWechat = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(WECHAT_ID);
      }
      setPsychActivationMsg({ ok: true, text: '微信号已复制。' });
    } catch {
      setPsychActivationMsg({ ok: false, text: `复制失败，请手动添加 ${WECHAT_ID}` });
    }
  };

  const handlePsychActivate = async () => {
    const user = getStoredUser();
    if (!user?.userId) {
      setPsychActivationMsg({ ok: false, text: '请先登录后再激活。' });
      if (onRequireLogin) onRequireLogin();
      return;
    }
    const normalized = psychActivationCode.trim().toUpperCase();
    if (normalized.length < 6) {
      setPsychActivationMsg({ ok: false, text: '请输入有效激活码（至少 6 位）。' });
      return;
    }
    setPsychActivationLoading(true);
    setPsychActivationMsg({ ok: true, text: '激活中，请稍候…' });
    try {
      await activateMembershipCode(normalized);
      setPsychActivationMsg({ ok: true, text: '激活成功，已解锁心理测试报告。' });
      setPsychActivationOpen(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : '激活失败';
      setPsychActivationMsg({ ok: false, text: msg });
      if (msg.includes('请先登录') && onRequireLogin) onRequireLogin();
    } finally {
      setPsychActivationLoading(false);
    }
  };

  const renderPsychActivationModal = () => (
    psychActivationOpen ? (
      <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4">
        <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl p-5 relative">
          <button
            onClick={() => setPsychActivationOpen(false)}
            className="absolute right-3 top-3 rounded-full p-1.5 text-slate-500 hover:bg-slate-100"
          >
            ×
          </button>
          <h3 className="text-base font-bold text-slate-900">心理测试报告解锁</h3>
          <p className="mt-2 text-sm text-slate-600">当前账号免费次数已用完，请添加轩哥微信获取激活码并填写后查看结果。</p>
          <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
            轩哥微信：
            <button onClick={copyWechat} className="ml-1 font-semibold text-indigo-600 hover:text-indigo-700">{WECHAT_ID}</button>
          </div>
          <div className="mt-3 flex gap-2">
            <input
              type="text"
              value={psychActivationCode}
              onChange={(e) => setPsychActivationCode(e.target.value)}
              placeholder="请输入激活码"
              className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <button
              onClick={handlePsychActivate}
              disabled={psychActivationLoading}
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-70"
            >
              {psychActivationLoading ? '提交中...' : '立即激活'}
            </button>
          </div>
          {psychActivationMsg && (
            <div className={`mt-3 rounded-lg border px-3 py-2 text-sm ${psychActivationMsg.ok ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-rose-200 bg-rose-50 text-rose-700'}`}>
              {psychActivationMsg.text}
            </div>
          )}
        </div>
      </div>
    ) : null
  );

  // 九型人格：嵌入现有 EnneagramTest 组件
  if (view === 'enneagram') {
    return (
      <>
        <EnneagramTest
          onBack={goBackFromEnneagram}
          onRequireLogin={onRequireLogin}
          previewMode={enneagramPreviewMode}
          onBeforeShowResult={ensurePsychResultAccessWithPending}
        />
        {renderPsychActivationModal()}
      </>
    );
  }

  if (view === 'mbti_stage') {
    return (
      <>
        <MbtiStageTest onBack={goBackFromStandaloneTest} onBeforeShowResult={ensurePsychResultAccessWithPending} />
        {renderPsychActivationModal()}
      </>
    );
  }

  if (view === 'beauty') {
    return (
      <>
        <BeautyFaceTest onBack={goBackFromStandaloneTest} onRequireLogin={onRequireLogin} onBeforeShowResult={ensurePsychResultAccessWithPending} />
        {renderPsychActivationModal()}
      </>
    );
  }

  const handleHeaderBack = () => {
    if (initialTestId) onBack();
    else if (view === 'home') onBack();
    else { setView('home'); setActiveTest(null); }
  };

  const activeMeta = TEST_META.find((t) => t.id === activeTest);
  const resolvedTitle = activeTest
    ? (TOPBAR_SHORT_TITLE[String(activeTest || '')] || activeMeta?.title || '心理测评')
    : '心理测评';
  const topBarTitle = `轩辕国学·${resolvedTitle}`;
  const showSbtiShare = activeTest === 'sbti' && view !== 'home';
  const activeQuizQuestions = view === 'quiz' ? getActiveQuestions(activeTest) : [];

  return (
    <div className="min-h-screen bg-[radial-gradient(120%_95%_at_50%_-12%,#f2dfbd_0%,#eee3d0_36%,#eceff5_72%,#eef2f8_100%)] font-sans text-gray-800 flex flex-col">
      <XuanYuanTopBar
        title={topBarTitle}
        onBack={handleHeaderBack}
        variant="lightBlend"
        right={showSbtiShare ? (
          <button
            type="button"
            onClick={() => {
              void shareSbtiLink(view === 'result' ? 'result' : 'entry');
            }}
            className="inline-flex items-center gap-1 rounded-full bg-white/80 border border-white px-3 py-1.5 text-[12px] font-bold text-gray-700 shadow-sm hover:bg-white"
            aria-label="分享 SBTI"
          >
            <Share2 size={14} />
            <span>分享</span>
          </button>
        ) : undefined}
      />
      <div className="flex-1 overflow-y-auto">
        {view === 'home' && (
          <div className="p-6 pb-20">
            <div className="mb-8 text-center">
              <h2 className="text-2xl font-extrabold text-gray-900 mb-2">探索潜意识</h2>
              <div className="w-12 h-1 bg-blue-500 mx-auto rounded-full"></div>
              <p className="text-xs text-gray-400 mt-4 tracking-widest uppercase">深度测评量表库</p>
            </div>
            <div className="space-y-4">
              {TEST_META.map((test) => (
                <div key={test.id} onClick={() => selectTest(test.id)} className="group rounded-2xl border border-white/75 bg-white/78 p-4 shadow-[0_8px_18px_rgba(120,98,64,0.08)] backdrop-blur-sm transition-all hover:shadow-[0_10px_22px_rgba(120,98,64,0.12)] cursor-pointer flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <span className={`w-3 h-3 rounded-full ${test.color.split(' ')[0]}`}></span>
                    <div>
                      <h3 className="font-bold text-[15px] text-gray-800 group-hover:text-blue-600">{test.title}</h3>
                      <span className="text-[10px] text-gray-400 line-clamp-1">{test.desc}</span>
                    </div>
                  </div>
                  <ChevronRight size={20} className="text-gray-300 group-hover:text-blue-500" />
                </div>
              ))}
            </div>
          </div>
        )}

        {view === 'intro' && activeTest && (
          <IntroView
            meta={TEST_META.find((t) => t.id === activeTest)!}
            historyRecords={activeHistoryRecords}
            onOpenHistory={openHistoryRecord}
            onStart={startTest}
            onViewSample={handleViewSampleReport}
            onBack={initialTestId ? onBack : resetHome}
          />
        )}

        {view === 'quiz' && activeTest && activeQuizQuestions.length > 0 && (
          <QuizView
            testId={activeTest}
            qIndex={answeredScores.length}
            questions={activeQuizQuestions}
            onAnswer={handleAnswer}
            onPrev={handlePrevQuestion}
            onBack={initialTestId ? onBack : resetHome}
          />
        )}

        {view === 'result' && activeTest && (
          <ResultView
            testId={activeTest}
            scores={scores}
            recentAnswers={answeredScores}
            questions={activeQuizQuestions}
            sbtiResult={sbtiResult}
            sbtiAnswers={sbtiAnswers}
            onBack={resetHome}
            getAvg={getAvg}
            getScorePct={getScorePct}
          />
        )}
      </div>
      {renderPsychActivationModal()}
    </div>
  );
}

function IntroView({
  meta,
  historyRecords,
  onOpenHistory,
  onStart,
  onViewSample,
  onBack,
}: {
  meta: { id: string; title: string; desc: string; time: string; count: number; color: string };
  historyRecords: PsychHistoryRecord[];
  onOpenHistory: (record: PsychHistoryRecord) => void;
  onStart: () => void;
  onViewSample: () => void;
  onBack: () => void;
}) {
  const introText = TEST_INTRO_BENEFITS[meta.id] || meta.desc;
  const compactIntro = meta.id === 'mbti';
  return (
    <div className="flex-1 flex flex-col bg-transparent p-6">
      <div className="flex-1 flex flex-col justify-center items-center text-center">
        <div className={`${compactIntro ? 'w-16 h-16 mb-4' : 'w-20 h-20 mb-6'} rounded-full flex items-center justify-center shadow-inner ${meta.color.split(' ')[0]}`}>
          <FileText size={compactIntro ? 30 : 40} className={meta.color.split(' ')[1]} />
        </div>
        <h2 className={`${compactIntro ? 'text-xl mb-3' : 'text-2xl mb-4'} font-black text-gray-900`}>{meta.title}</h2>
        <p className={`${compactIntro ? 'text-xs mb-6' : 'text-sm mb-8'} text-gray-500 leading-relaxed px-4`}>{introText}</p>
        <div className={`flex space-x-8 border border-white/75 bg-white/60 backdrop-blur-sm ${compactIntro ? 'px-5 py-3 mb-8' : 'px-6 py-4 mb-10'} rounded-2xl w-full justify-center`}>
          <div className="flex flex-col items-center"><span className="text-gray-400 text-xs mb-1">精选题库</span><span className={`${compactIntro ? 'text-base' : 'text-lg'} font-bold text-gray-800`}>{meta.count} 题</span></div>
          <div className="w-px bg-gray-200"></div>
          <div className="flex flex-col items-center"><span className="text-gray-400 text-xs mb-1">预计耗时</span><span className={`${compactIntro ? 'text-base' : 'text-lg'} font-bold text-gray-800`}>{meta.time}</span></div>
        </div>
        <button onClick={onStart} className={`w-full ${compactIntro ? 'py-3 text-base' : 'py-4 text-lg'} rounded-full bg-blue-600 text-white font-bold shadow-lg hover:bg-blue-700`}>开始测试</button>
        <button onClick={onViewSample} className={`w-full mt-4 ${compactIntro ? 'py-2.5 text-[13px]' : 'py-3 text-sm'} rounded-full bg-gray-100 text-gray-600 font-bold hover:bg-gray-200`}>查看报告样例</button>
        {historyRecords.length > 0 && (
          <div className="w-full mt-6 rounded-2xl border border-white/70 bg-white/60 p-4 text-left backdrop-blur-sm">
            <div className="text-sm font-bold text-gray-800 mb-3">历史报告</div>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {historyRecords.map((record) => (
                <button
                  key={record.id}
                  onClick={() => onOpenHistory(record)}
                  className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-left hover:border-blue-300 hover:bg-blue-50/40"
                >
                  <div className="text-xs text-gray-500">{formatHistoryTime(record.createdAt)}</div>
                  <div className="text-sm font-semibold text-gray-800 mt-0.5">{record.summary || `${meta.title} 报告`}</div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function QuizView({
  testId,
  qIndex,
  questions,
  onAnswer,
  onPrev,
  onBack,
}: {
  testId: string;
  qIndex: number;
  questions: Question[];
  onAnswer: (answer: { score: Record<string, number>; questionId?: number; label?: string }) => void;
  onPrev?: () => void;
  onBack: () => void;
}) {
  const q = questions[qIndex];
  const compactQuestion = testId === 'mbti';
  const totalQuestions = Math.max(1, questions.length);
  const progress = Math.min(100, (Math.min(qIndex + 1, questions.length) / totalQuestions) * 100);
  const meta = TEST_META.find((t) => t.id === testId)!;
  const canGoPrev = qIndex > 0 && onPrev;

  if (!q) {
    return (
      <div className="flex-1 flex flex-col bg-slate-50">
        <div className="w-full h-1 bg-gray-200">
          <div className="h-full bg-blue-500 transition-all duration-300" style={{ width: `${progress}%` }}></div>
        </div>
        <div className="flex-1 p-6 flex flex-col items-center justify-center text-center">
          <h2 className="text-lg font-bold text-gray-800">正在生成报告</h2>
          <p className="mt-2 text-sm text-gray-500">请稍候，马上展示你的测试结果…</p>
        </div>
      </div>
    );
  }

  const safeOptions: QuestionOption[] = Array.isArray(q.options)
    ? q.options.filter((opt): opt is QuestionOption => Boolean(opt && typeof opt.text === 'string' && typeof opt.score === 'object' && opt.score !== null))
    : [];

  return (
    <div className="flex-1 flex flex-col bg-slate-50">
      <div className="w-full h-1 bg-gray-200"><div className="h-full bg-blue-500 transition-all duration-300" style={{ width: `${progress}%` }}></div></div>
      <div className="flex-1 p-6 flex flex-col overflow-y-auto">
        <span className="text-xs font-bold text-blue-500 mb-3 tracking-widest uppercase">进度 {qIndex + 1} / {questions.length}</span>
        <h2 className={`${compactQuestion ? 'text-lg mb-6' : 'text-xl mb-8'} font-bold text-gray-800 leading-relaxed`}>{q.text}</h2>
        <div className="space-y-3 mt-auto mb-4">
          {safeOptions.length > 0 ? safeOptions.map((opt, idx) => (
            <button
              key={idx}
              onClick={(event) => {
                event.currentTarget.blur();
                onAnswer({ score: opt.score, questionId: q.id, label: opt.label });
              }}
              className={`w-full ${compactQuestion ? 'p-3.5 text-[15px]' : 'p-4'} rounded-xl bg-white border border-gray-200 text-left active:border-blue-300 active:bg-blue-50/60 md:hover:border-blue-400 md:hover:bg-blue-50 font-medium text-gray-700 shadow-sm`}
            >
              {opt.text}
            </button>
          )) : (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-700">
              当前题目选项异常，请返回后重试。
            </div>
          )}
        </div>
        {canGoPrev && (
          <button onClick={onPrev} className="w-full py-3 rounded-xl bg-gray-100 border border-gray-200 text-gray-600 font-medium text-sm hover:bg-gray-200 transition-colors mt-4">
            上一题
          </button>
        )}
      </div>
    </div>
  );
}

function QRBanner({
  qrDataUrl,
  className,
  leftAction,
}: {
  qrDataUrl: string | null;
  className?: string;
  leftAction?: React.ReactNode;
}) {
  return (
    <XuanYuanReportFooter
      qrDataUrl={qrDataUrl}
      textClassName="text-gray-600"
      emphasizeClassName="text-gray-700"
      qrBorderClassName="border-gray-200"
      className={className || 'border-gray-100'}
      leftAction={leftAction}
    />
  );
}

function ResultView({
  testId,
  scores,
  recentAnswers,
  questions,
  sbtiResult,
  sbtiAnswers,
  onBack,
  getAvg,
  getScorePct,
}: {
  testId: string;
  scores: ScoreRecord;
  recentAnswers: Array<Record<string, number>>;
  questions: Question[];
  sbtiResult?: SbtiResult | null;
  sbtiAnswers?: Array<{ questionId: number; label: string }>;
  onBack: () => void;
  getAvg: (s: ScoreRecord, k: string) => number;
  getScorePct: (s: ScoreRecord, k: string, max?: number) => number;
}) {
  const meta = TEST_META.find((t) => t.id === testId)!;
  const reportRef = useRef<HTMLDivElement>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);

  useEffect(() => {
    const shareUrl = testId === 'sbti'
      ? `${SITE_URL}/?tool=psychSbti&tab=practice`
      : SITE_URL;
    QRCode.toDataURL(shareUrl, { width: 200, margin: 1 }).then(setQrDataUrl).catch(() => {});
  }, [testId]);

  const downloadImage = () => {
    if (!(window as unknown as { html2canvas?: (el: HTMLElement, opt?: unknown) => Promise<HTMLCanvasElement> }).html2canvas) {
      alert('生成引擎正在加载，请稍等...');
      return;
    }
    setIsCapturing(true);
    setTimeout(() => {
      (window as unknown as { html2canvas: (el: HTMLElement, opt?: unknown) => Promise<HTMLCanvasElement> })
        .html2canvas(reportRef.current!, { scale: 2, useCORS: true, backgroundColor: '#f9fafb' })
        .then((canvas) => {
          const url = canvas.toDataURL('image/png');
          const link = document.createElement('a');
          link.download = `我的【${meta.title}】解析报告.png`;
          link.href = url;
          link.click();
          setIsCapturing(false);
        });
    }, 400);
  };

  const hasAnyScores = Object.values(scores).some((item) => (item?.count || 0) > 0);

  if (testId !== 'sbti' && !hasAnyScores) {
    return (
      <div className="p-4 pb-24">
        <div ref={reportRef} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
          <h2 className="text-xl font-bold text-gray-800">{meta.title}</h2>
          <p className="text-gray-500 mt-2">当前结果数据为空，请重新完成测试后查看报告。</p>
        </div>
      </div>
    );
  }

  if (testId === 'sbti') {
    const resolvedSbtiResult = sbtiResult || computeSbtiResult(sbtiAnswers || []);
    if (!resolvedSbtiResult) {
      return (
        <div className="p-4 pb-24">
          <div ref={reportRef} className="rounded-2xl border border-white/70 bg-white/60 backdrop-blur-sm p-6 shadow-sm">
            <h2 className="text-xl font-bold text-gray-800">SBTI 人格报告</h2>
            <p className="text-gray-500 mt-2">未找到可展示结果，请重新开始测试。</p>
          </div>
        </div>
      );
    }

    const type = resolvedSbtiResult.type;
    const imageSrc = SBTI_TYPE_IMAGES[type.code];
    const dimEntries = SBTI_DIMENSION_ORDER.map((dimKey) => {
      const level = resolvedSbtiResult.levels?.[dimKey] || 'M';
      const score = resolvedSbtiResult.rawScores?.[dimKey] ?? 0;
      const metaInfo = SBTI_DIMENSIONS.find((d) => d.key === dimKey);
      const explanation = level === 'L' ? metaInfo?.lLabel : level === 'H' ? metaInfo?.hLabel : metaInfo?.mLabel;
      const scorePct = Math.max(0, Math.min(100, Math.round(((score - 2) / 4) * 100)));
      const levelBarClass = level === 'H'
        ? 'from-emerald-400 to-teal-500'
        : level === 'M'
          ? 'from-amber-400 to-orange-500'
          : 'from-slate-400 to-slate-500';
      return {
        key: dimKey,
        name: metaInfo?.label || dimKey,
        level,
        score,
        scorePct,
        levelBarClass,
        explanation: explanation || '',
      };
    });

    return (
      <div className="p-4 pb-24 bg-gradient-to-b from-blue-50/50 to-white">
        <div ref={reportRef} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-white/80 bg-white/75 p-3 backdrop-blur-sm">
              {imageSrc ? (
                <img src={imageSrc} alt={`${type.code}（${type.cn}）`} className="w-full rounded-xl border border-gray-200 bg-white object-contain" />
              ) : (
                <div className="h-48 rounded-xl border border-dashed border-gray-300 flex items-center justify-center text-sm text-gray-500 bg-white">暂无类型海报</div>
              )}
              <p className="text-xs text-gray-500 mt-2">{type.intro}</p>
            </div>
            <div className="rounded-2xl border border-white/80 bg-white/75 p-4 backdrop-blur-sm">
              <h4 className="font-bold text-gray-800 mb-2">人格说明</h4>
              <p className="text-sm leading-relaxed text-gray-700">{type.desc}</p>
              <p className="mt-3 text-xs text-blue-700">稀有度：{type.rarity} · 占比约 {type.rarityPercent}%</p>
            </div>
          </div>

          <div className="rounded-xl border border-white/80 bg-white/70 px-3 py-3 space-y-2">
            <button
              type="button"
              onClick={() => { window.location.assign(SBTI_SHARE_URL); }}
              className="w-full rounded-xl bg-gradient-to-r from-sky-500 to-blue-500 text-white font-bold py-3 shadow-sm hover:from-sky-600 hover:to-blue-600"
            >
              我也要测 SBTI
            </button>
            <button
              type="button"
              onClick={onBack}
              className="w-full rounded-xl bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-bold py-3 shadow-sm hover:from-blue-600 hover:to-indigo-600"
            >
              点击进行其他测试
            </button>
          </div>

          <div className="rounded-2xl border border-white/80 bg-white/75 p-4 backdrop-blur-sm">
            <h4 className="font-bold text-gray-800 mb-3">15 维度明细</h4>
            <div className="space-y-3">
              {dimEntries.map((item) => (
                <div key={item.key} className="rounded-xl border border-gray-200 bg-white px-3 py-2.5">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-semibold text-gray-800">{item.name}</span>
                    <span className="font-bold text-blue-700">{item.level} / {item.score}分</span>
                  </div>
                  <div className="mt-2 h-2.5 w-full rounded-full bg-gray-200 overflow-hidden">
                    <div
                      className={`h-full rounded-full bg-gradient-to-r ${item.levelBarClass}`}
                      style={{ width: `${item.scorePct}%` }}
                    ></div>
                  </div>
                  <div className="mt-1 flex items-center justify-between text-[10px] text-gray-500">
                    <span>低</span>
                    <span>中</span>
                    <span>高</span>
                  </div>
                  <p className="mt-1 text-xs text-gray-600 leading-relaxed">{item.explanation}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
        <button onClick={downloadImage} className="fixed bottom-6 left-6 right-6 max-w-md mx-auto bg-blue-600 hover:bg-blue-700 text-white rounded-full py-4 font-bold shadow-lg flex items-center justify-center">
          <Download size={20} className="mr-2" />保存报告原图
        </button>
      </div>
    );
  }

  if (testId === 'mbti') {
    const getSum = (key: string) => (scores[key] ? scores[key].total : 0);
    const E = getSum('E'), I = getSum('I'), S = getSum('S'), N = getSum('N'), T = getSum('T'), F = getSum('F'), J = getSum('J'), P = getSum('P');
    const type = `${E >= I ? 'E' : 'I'}${S >= N ? 'S' : 'N'}${T >= F ? 'T' : 'F'}${J >= P ? 'J' : 'P'}`;
    const typeInfo = MBTI_DATA.types[type as keyof typeof MBTI_DATA.types] || { name: '未知', desc: '暂无数据' };
    const renderBar = (leftChar: string, leftLabel: string, rightChar: string, rightLabel: string, leftScore: number, rightScore: number, colorClass: string) => {
      const total = leftScore + rightScore || 1;
      const leftPct = Math.round((leftScore / total) * 100);
      const rightPct = 100 - leftPct;
      return (
        <div className="mb-5">
          <div className="flex justify-between text-xs font-bold text-gray-700 mb-2">
            <span>{leftChar} {leftLabel} ({leftPct}%)</span>
            <span>({rightPct}%) {rightLabel} {rightChar}</span>
          </div>
          <div className="w-full h-3.5 bg-gray-200 rounded-full flex overflow-hidden">
            <div style={{ width: `${leftPct}%` }} className={`h-full ${colorClass}`}></div>
            <div style={{ width: `${rightPct}%` }} className="h-full bg-gray-300"></div>
          </div>
        </div>
      );
    };
    return (
      <div className="p-4 pb-24">
        <div ref={reportRef} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <div className="text-center mb-8 pt-2">
            <h3 className="text-gray-500 text-xs font-bold mb-3 tracking-widest uppercase">你的灵魂人格类型</h3>
            <h1 className="text-5xl font-black tracking-widest text-amber-500 mb-4">{type}</h1>
            <span className="bg-amber-50 text-amber-600 px-5 py-2 rounded-full text-sm font-black">{typeInfo.name}</span>
          </div>
          <div className="bg-gray-50 rounded-xl p-5 mb-8">
            {renderBar('E', '外向', 'I', '内向', E, I, 'bg-amber-400')}
            {renderBar('S', '实感', 'N', '直觉', S, N, 'bg-blue-400')}
            {renderBar('T', '思考', 'F', '情感', T, F, 'bg-emerald-400')}
            {renderBar('J', '判断', 'P', '知觉', J, P, 'bg-sky-400')}
          </div>
          <div className="bg-gray-800 text-white rounded-xl p-5">
            <div className="flex items-center mb-3"><span className="w-1.5 h-5 bg-amber-500 rounded-full mr-2"></span><h4 className="font-bold text-lg">性格解析画像</h4></div>
            <p className="text-sm leading-relaxed text-gray-200">{typeInfo.desc}</p>
          </div>
          <QRBanner qrDataUrl={qrDataUrl} />
        </div>
        <button onClick={downloadImage} className="fixed bottom-6 left-6 right-6 max-w-md mx-auto bg-blue-600 hover:bg-blue-700 text-white rounded-full py-4 font-bold shadow-lg flex items-center justify-center">
          <Download size={20} className="mr-2" />保存报告原图
        </button>
      </div>
    );
  }

  if (testId === 'scl90') {
    const data = ['f1', 'f2', 'f3', 'f4', 'f5', 'f6', 'f7', 'f8', 'f9'].map((k, i) => ({
      subject: ['躯体化', '强迫', '人际敏感', '抑郁', '焦虑', '敌对', '恐怖', '偏执', '精神病性'][i],
      val: getAvg(scores, k).toFixed(2),
    }));
    let totalScore = 0, totalCount = 0;
    Object.keys(scores).forEach((k) => { totalScore += scores[k].total; totalCount += scores[k].count; });
    const globalAvg = (totalCount > 0 ? totalScore / totalCount : 1).toFixed(2);
    const positiveFactors = data.filter((d) => parseFloat(d.val) >= 2.0).length;
    return (
      <div className="p-4 pb-24">
        <div ref={reportRef} className="bg-white rounded-2xl p-5 shadow-sm border border-teal-50">
          <div className="flex justify-between items-center mb-6 bg-teal-50/50 p-4 rounded-xl">
            <div className="text-center"><p className="text-xs text-teal-600 font-bold mb-1">全局严重指数</p><p className="text-3xl font-black text-teal-700">{globalAvg}</p></div>
            <div className="w-px h-10 bg-teal-200"></div>
            <div className="text-center"><p className="text-xs text-teal-600 font-bold mb-1">阳性项目数(≥2分)</p><p className="text-3xl font-black text-teal-700">{positiveFactors}</p></div>
          </div>
          <h3 className="font-bold text-gray-800 mb-4">9大核心因子</h3>
          <div className="h-64 w-full mb-6">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart cx="50%" cy="50%" outerRadius="65%" data={data}>
                <PolarGrid stroke="#ccfbf1" />
                <PolarAngleAxis dataKey="subject" tick={{ fill: '#0f766e', fontSize: 10 }} />
                <PolarRadiusAxis domain={[1, 5]} tick={false} axisLine={false} />
                <Radar name="你的得分" dataKey="val" stroke="#0d9488" strokeWidth={2} fill="#99f6e4" fillOpacity={0.4} dot={{ r: 3, fill: '#0f766e' }} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-3 mb-4">
            {data.map((item, idx) => (
              <div key={idx} className="flex items-center text-sm">
                <div className="w-20 text-gray-700 font-bold text-xs">{item.subject}</div>
                <div className="flex-1 bg-gray-100 rounded-full h-2.5 mx-2 relative overflow-hidden">
                  <div className={`absolute left-0 top-0 bottom-0 rounded-full ${parseFloat(item.val) >= 2.0 ? 'bg-red-400' : 'bg-teal-400'}`} style={{ width: `${((parseFloat(item.val) - 1) / 4) * 100}%` }}></div>
                </div>
                <div className={`w-10 text-right font-black text-sm ${parseFloat(item.val) >= 2.0 ? 'text-red-500' : 'text-teal-600'}`}>{item.val}</div>
              </div>
            ))}
          </div>
          <QRBanner qrDataUrl={qrDataUrl} />
        </div>
        <button onClick={downloadImage} className="fixed bottom-6 left-6 right-6 max-w-md mx-auto bg-blue-600 hover:bg-blue-700 text-white rounded-full py-4 font-bold shadow-lg flex items-center justify-center">
          <Download size={20} className="mr-2" />保存报告原图
        </button>
      </div>
    );
  }

  if (testId === 'bipolar') {
    const pct = getScorePct(scores, 'bipolar');
    let riskLevel = '低风险', riskColor = 'text-emerald-500', barColor = 'bg-emerald-400';
    if (pct > 75) { riskLevel = '高风险报警'; riskColor = 'text-red-600'; barColor = 'bg-red-500'; } else if (pct > 50) { riskLevel = '中度波动'; riskColor = 'text-orange-500'; barColor = 'bg-orange-400'; }
    return (
      <div className="p-4 pb-24">
        <div ref={reportRef} className="bg-white rounded-2xl p-6 shadow-sm border border-cyan-50">
          <div className="flex flex-col items-center justify-center mb-8">
            <AlertTriangle size={48} className={`${riskColor} mb-4 opacity-80`} />
            <h3 className="text-gray-500 text-xs font-bold tracking-widest uppercase mb-2">双相情感障碍(躁郁) 筛查结果</h3>
            <h1 className={`text-4xl font-black tracking-wider ${riskColor}`}>{riskLevel}</h1>
          </div>
          <div className="mb-8">
            <div className="w-full h-4 bg-gray-100 rounded-full overflow-hidden">
              <div className={`h-full ${barColor}`} style={{ width: `${pct}%` }}></div>
            </div>
            <div className="text-center mt-2 font-black text-xl text-gray-700">{pct}%</div>
          </div>
          <div className="bg-gray-800 text-white rounded-xl p-5">
            <h4 className="font-bold text-sm mb-2">结果解析</h4>
            <p className="text-xs leading-relaxed text-gray-300">
              {pct > 75 ? '您的情绪波动幅度极大，建议寻求专业精神科医生的帮助。' : pct > 50 ? '您的情绪起伏较常人更为剧烈，建议近期多关注自己的睡眠与压力源。' : '您的情绪处于相对稳定和健康的区间。'}
            </p>
          </div>
          <QRBanner qrDataUrl={qrDataUrl} />
        </div>
        <button onClick={downloadImage} className="fixed bottom-6 left-6 right-6 max-w-md mx-auto bg-blue-600 hover:bg-blue-700 text-white rounded-full py-4 font-bold shadow-lg flex items-center justify-center">
          <Download size={20} className="mr-2" />保存报告原图
        </button>
      </div>
    );
  }

  if (testId === 'sex_repress') {
    const pct = getScorePct(scores, 'repress');
    let level = '健康自洽', desc = '您对自身的性需求和身体有着非常健康且自洽的接纳度。';
    let color = 'text-pink-500';
    if (pct > 75) { level = '重度压抑'; desc = '您的潜意识中对性有着极强的道德包袱和羞耻感。'; color = 'text-rose-700'; }
    else if (pct > 50) { level = '中度回避'; desc = '您在性观念上相对保守，难以向伴侣坦诚自己真实的偏好。'; color = 'text-rose-500'; }
    else if (pct < 25) { level = '极度开放'; desc = '您完全打破了世俗对性的枷锁，敢于主张自己的身体需求。'; color = 'text-fuchsia-500'; }
    return (
      <div className="p-4 pb-24">
        <div ref={reportRef} className="bg-white rounded-2xl p-6 shadow-sm border border-pink-50 text-center">
          <h3 className="text-gray-500 text-sm font-bold mb-6 tracking-widest uppercase">您的性压抑指数</h3>
          <div className="relative w-48 h-48 mx-auto mb-6 flex items-center justify-center rounded-full border-8 border-pink-50">
            <div className="flex flex-col items-center z-10 bg-white w-36 h-36 rounded-full justify-center shadow-lg">
              <span className={`text-5xl font-black ${color}`}>{pct}%</span>
            </div>
          </div>
          <h1 className={`text-2xl font-black mb-3 ${color}`}>{level}</h1>
          <p className="text-sm text-gray-600 leading-relaxed text-justify px-2 bg-pink-50/30 p-4 rounded-xl">{desc}</p>
          <QRBanner qrDataUrl={qrDataUrl} />
        </div>
        <button onClick={downloadImage} className="fixed bottom-6 left-6 right-6 max-w-md mx-auto bg-blue-600 hover:bg-blue-700 text-white rounded-full py-4 font-bold shadow-lg flex items-center justify-center">
          <Download size={20} className="mr-2" />保存报告原图
        </button>
      </div>
    );
  }

  if (testId === 'quirk') {
    const pct = getScorePct(scores, 'quirk');
    const rankedDims = QUIRK_DIMENSIONS
      .map((item) => ({ ...item, val: getScorePct(scores, item.key) }))
      .sort((a, b) => b.val - a.val);
    const radarData = rankedDims.map((item) => ({ subject: item.label, A: item.val }));
    const topThree = rankedDims.slice(0, 3);
    const level = pct >= 75 ? '高敏强迫型' : pct >= 50 ? '中度怪癖型' : '低压松弛型';
    const levelDesc = pct >= 75
      ? '你在秩序、边界和控制感上明显偏高，容易出现“必须按我的方式来”的状态。'
      : pct >= 50
        ? '你在部分场景会出现明显执着，但整体仍能根据环境调整。'
        : '你的日常怪癖负担较低，更多是习惯偏好而非强迫需求。';
    return (
      <div className="p-4 pb-24">
        <div ref={reportRef} className="bg-white rounded-2xl p-6 shadow-sm border border-yellow-100">
          <div className="text-center mb-6">
            <h3 className="text-gray-500 text-sm font-bold mb-2 tracking-widest uppercase">心理怪癖雷达</h3>
            <div className="flex items-center justify-center mt-4">
              <span className="text-6xl font-black text-amber-500">{pct}</span>
              <span className="text-xl text-amber-400 font-bold ml-1">%</span>
            </div>
            <p className="mt-2 text-sm font-bold text-amber-700">{level}</p>
          </div>
          <div className="w-full h-4 bg-gray-100 rounded-full overflow-hidden mb-6">
            <div className="h-full bg-gradient-to-r from-yellow-300 to-amber-500" style={{ width: `${pct}%` }}></div>
          </div>
          <div className="h-56 w-full mb-5">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart cx="50%" cy="50%" outerRadius="68%" data={radarData}>
                <PolarGrid stroke="#fef3c7" />
                <PolarAngleAxis dataKey="subject" tick={{ fill: '#92400e', fontSize: 10 }} />
                <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                <Radar dataKey="A" stroke="#d97706" strokeWidth={2} fill="#fbbf24" fillOpacity={0.3} dot={{ r: 2.5, fill: '#b45309' }} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-yellow-50 rounded-xl p-4">
            <h4 className="font-bold text-gray-800 text-base mb-2">结果速读</h4>
            <p className="text-sm leading-relaxed text-gray-600">{levelDesc}</p>
            <div className="mt-3 grid grid-cols-1 gap-2">
              {topThree.map((item) => (
                <div key={item.key} className="rounded-lg border border-yellow-200 bg-white/85 px-3 py-2 text-sm text-gray-700">
                  {item.simple}（{item.val}%）
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs text-amber-700">提示：该测试用于倾向识别，不等同医学诊断。</p>
          </div>
          <QRBanner qrDataUrl={qrDataUrl} />
        </div>
        <button onClick={downloadImage} className="fixed bottom-6 left-6 right-6 max-w-md mx-auto bg-blue-600 hover:bg-blue-700 text-white rounded-full py-4 font-bold shadow-lg flex items-center justify-center">
          <Download size={20} className="mr-2" />保存报告原图
        </button>
      </div>
    );
  }

  if (testId === 'preference') {
    const preferenceScoreMap = getPreferenceScoreMap(scores);
    const data = PREFERENCE_TRAITS.map((item) => ({
      subject: item.label,
      A: Number(preferenceScoreMap[item.key] || 0),
    }));
    const ranked = [...data].sort((a, b) => b.A - a.A);
    const allTraits = ranked;
    const topLabel = ranked.slice(0, 3).map((item) => item.subject).join(' / ');
    return (
      <div className="p-4 pb-24">
        <div ref={reportRef} className="bg-white rounded-2xl p-5 shadow-sm border border-cyan-100">
          <div className="text-center mb-5">
            <h3 className="text-gray-500 text-sm font-bold tracking-widest uppercase">性偏好倾向雷达</h3>
            <h1 className="mt-2 text-2xl font-black text-cyan-700">{topLabel || '待解析'}</h1>
          </div>
          <div className="h-64 w-full mb-6">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart cx="50%" cy="50%" outerRadius="70%" data={data}>
                <PolarGrid stroke="#bae6fd" />
                <PolarAngleAxis dataKey="subject" tick={{ fill: '#0e7490', fontSize: 9 }} />
                <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                <Radar dataKey="A" stroke="#0ea5e9" strokeWidth={2} fill="#38bdf8" fillOpacity={0.22} dot={{ r: 2.5, fill: '#0369a1' }} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-2">
            {allTraits.map((item, idx) => (
              <div key={`${item.subject}-${idx}`} className="flex items-center text-sm">
                <div className="w-24 shrink-0 text-cyan-700 font-semibold">{item.subject}</div>
                <div className="flex-1 bg-cyan-50 rounded-full h-3 mx-2 overflow-hidden min-w-0">
                  <div className="h-full bg-cyan-500 rounded-full" style={{ width: `${item.A}%` }} />
                </div>
                <div className="w-11 shrink-0 text-right text-cyan-700 font-black">{item.A}%</div>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-gray-500 leading-relaxed">
            该结果用于沟通偏好和关系边界，不代表价值判断；请在“自愿、平等、安全”的前提下理解和使用。
          </p>
          <QRBanner qrDataUrl={qrDataUrl} />
        </div>
        <button onClick={downloadImage} className="fixed bottom-6 left-6 right-6 max-w-md mx-auto bg-blue-600 hover:bg-blue-700 text-white rounded-full py-4 font-bold shadow-lg flex items-center justify-center">
          <Download size={20} className="mr-2" />保存报告原图
        </button>
      </div>
    );
  }

  if (testId === 'city') {
    const calcMatch = (traits: string[]) => { let s = 100; traits.forEach((t) => { s -= (5 - getAvg(scores, t)) * 4; }); return Math.min(99, Math.max(20, Math.round(s))); };
    const cities = [
      { name: '上海', match: calcMatch(['modern', 'strive', 'diverse', 'culture']), tags: ['魔都', '快节奏'], desc: '极具包容性的国际大都市。' },
      { name: '北京', match: calcMatch(['history', 'strive', 'north', 'culture']), tags: ['帝都', '文化中心'], desc: '厚重的历史城墙与顶级的互联网大厂并存。' },
      { name: '广州', match: calcMatch(['diverse', 'strive', 'light', 'social']), tags: ['务实型', '早茶文化'], desc: '把"务实"刻进骨子里的城市。' },
      { name: '深圳', match: calcMatch(['modern', 'strive', 'south', 'diverse']), tags: ['鹏城', '创新力'], desc: '最年轻、最有活力的超级都市。' },
      { name: '成都', match: calcMatch(['comfort', 'social', 'spicy', 'culture']), tags: ['悠闲型', '美食之都'], desc: '一座来了就不想走的城市。' },
    ].sort((a, b) => b.match - a.match);
    return (
      <div className="p-4 pb-24">
        <div ref={reportRef} className="space-y-5">
          <h3 className="font-extrabold text-gray-800 mb-6 text-lg">与你灵魂最契合的城市 Top 5</h3>
          {cities.map((city, idx) => (
            <div key={idx} className="bg-white rounded-2xl p-5 shadow-sm border border-cyan-50">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center">
                  <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center mr-3"><span className="text-sm font-black">{idx + 1}</span></div>
                  <h4 className="text-xl font-black text-gray-900">{city.name}</h4>
                </div>
                <div className="bg-emerald-100 text-emerald-600 px-3 py-1 rounded-full text-lg font-black">{city.match}%</div>
              </div>
              <div className="flex flex-wrap gap-2 mb-3">{city.tags.map((t) => <span key={t} className="bg-blue-50 text-blue-600 px-2.5 py-1 rounded text-xs font-bold">{t}</span>)}</div>
              <p className="text-gray-600 text-sm leading-relaxed">{city.desc}</p>
            </div>
          ))}
          <div className="mt-6"><QRBanner qrDataUrl={qrDataUrl} /></div>
        </div>
        <button onClick={downloadImage} className="fixed bottom-6 left-6 right-6 max-w-md mx-auto bg-blue-600 hover:bg-blue-700 text-white rounded-full py-4 font-bold shadow-lg flex items-center justify-center"><Download size={20} className="mr-2" />保存报告原图</button>
      </div>
    );
  }
  if (testId === 'dark') {
    const data = [{ subject: '利己主义', A: getScorePct(scores, 'egoism') }, { subject: '马基亚维利', A: Math.max(getScorePct(scores, 'mach'), getScorePct(scores, 'mach2')) }, { subject: '自恋倾向', A: getScorePct(scores, 'narc') }, { subject: '权力渴望', A: getScorePct(scores, 'power') }, { subject: '精神变态', A: getScorePct(scores, 'psych') }, { subject: '施虐倾向', A: getScorePct(scores, 'sadism') }, { subject: '道德推脱', A: getScorePct(scores, 'moral') }, { subject: '恶毒倾向', A: getScorePct(scores, 'malice') }, { subject: '犬儒主义', A: getScorePct(scores, 'cynic') }];
    const avg = Math.round(data.reduce((a, c) => a + c.A, 0) / data.length);
    return (
      <div className="p-4 pb-24">
        <div ref={reportRef} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <div className="text-center mb-4"><h3 className="text-lg font-black bg-black text-white inline-block px-4 py-1 rounded">黑暗三角综合分析</h3></div>
          <div className="h-56 w-full mb-4"><ResponsiveContainer width="100%" height="100%"><RadarChart cx="50%" cy="50%" outerRadius="70%" data={data}><PolarGrid stroke="#e5e7eb" /><PolarAngleAxis dataKey="subject" tick={{ fill: '#4b5563', fontSize: 10 }} /><PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} /><Radar name="得分" dataKey="A" stroke="#0f766e" strokeWidth={2} fill="#14b8a6" fillOpacity={0.2} dot={{ r: 3, fill: '#0f766e' }} /></RadarChart></ResponsiveContainer></div>
          <div className="text-center mb-6"><div className="text-3xl font-black text-teal-600">{avg}%</div><div className="text-xs text-gray-400 font-bold">综合暗黑指数</div></div>
          <div className="space-y-2">{data.map((item, idx) => (<div key={idx} className="flex items-center text-sm"><div className="w-20 text-gray-700 font-bold text-xs">{item.subject}</div><div className="flex-1 bg-teal-50 rounded-full h-3 mx-2 relative overflow-hidden"><div className="absolute left-0 top-0 bottom-0 bg-teal-500 rounded-full" style={{ width: `${item.A}%` }}></div></div><div className="w-10 text-right text-teal-600 font-black text-sm">{item.A}%</div></div>))}</div>
          <QRBanner qrDataUrl={qrDataUrl} />
        </div>
        <button onClick={downloadImage} className="fixed bottom-6 left-6 right-6 max-w-md mx-auto bg-blue-600 hover:bg-blue-700 text-white rounded-full py-4 font-bold shadow-lg flex items-center justify-center"><Download size={20} className="mr-2" />保存报告原图</button>
      </div>
    );
  }
  if (testId === 'talent') {
    const getT = (k: string) => Math.round(getAvg(scores, k) * 4);
    const data = [{ subject: '语言', val: getT('lang') }, { subject: '逻辑', val: getT('logic') }, { subject: '空间', val: getT('space') }, { subject: '内省', val: getT('intro') }, { subject: '人际', val: getT('inter') }, { subject: '动觉', val: getT('body') }, { subject: '音乐', val: getT('music') }, { subject: '自然', val: getT('nature') }, { subject: '创造', val: getT('create') }, { subject: '美学', val: getT('aesthetic') }];
    return (
      <div className="p-4 pb-24">
        <div ref={reportRef} className="bg-[#f8fcff] rounded-2xl p-5 shadow-sm border border-sky-100">
          <h3 className="font-bold text-lg text-gray-800 mb-4">加德纳智能雷达图</h3>
          <div className="h-52 w-full mb-6"><ResponsiveContainer width="100%" height="100%"><RadarChart cx="50%" cy="50%" outerRadius="70%" data={data}><PolarGrid stroke="#dbeafe" /><PolarAngleAxis dataKey="subject" tick={{ fill: '#1d4ed8', fontSize: 10 }} /><PolarRadiusAxis domain={[0, 20]} tick={false} axisLine={false} /><Radar dataKey="val" stroke="#2563eb" strokeWidth={2} fill="transparent" dot={{ r: 3, fill: '#2563eb' }} /></RadarChart></ResponsiveContainer></div>
          <div className="space-y-2">{data.map((item, idx) => (<div key={idx} className="flex items-center text-sm"><div className="w-14 text-gray-600 font-medium">{item.subject}</div><div className="flex-1 bg-sky-50 rounded-full h-4 relative overflow-hidden mx-2"><div className="absolute left-0 top-0 bottom-0 bg-sky-500 rounded-full" style={{ width: `${(item.val / 20) * 100}%` }}></div><span className="absolute inset-y-0 left-2 z-10 flex items-center text-[10px] leading-none text-white font-bold">{item.val}分</span></div><div className="w-16 text-right text-xs text-gray-500">{item.val >= 16 ? '极度擅长' : item.val >= 12 ? '优势潜力' : '有待开发'}</div></div>))}</div>
          <QRBanner qrDataUrl={qrDataUrl} />
        </div>
        <button onClick={downloadImage} className="fixed bottom-6 left-6 right-6 max-w-md mx-auto bg-blue-600 hover:bg-blue-700 text-white rounded-full py-4 font-bold shadow-lg flex items-center justify-center"><Download size={20} className="mr-2" />保存报告原图</button>
      </div>
    );
  }
  if (testId === 'needs') {
    const top = [{ label: '意义感', val: getScorePct(scores, 'meaning'), color: 'bg-[#7CA1B4]' }, { label: '被爱欲', val: getScorePct(scores, 'love'), color: 'bg-[#D69C98]' }, { label: '精神连接', val: getScorePct(scores, 'connect'), color: 'bg-[#A396A9]' }, { label: '心智成长', val: getScorePct(scores, 'growth'), color: 'bg-[#98B0A3]' }, { label: '创造欲', val: getScorePct(scores, 'create'), color: 'bg-[#D2B48C]' }];
    const bottom = [{ label: '权力掌控', val: getScorePct(scores, 'power'), color: 'bg-[#8C8488]' }, { label: '享乐体验', val: getScorePct(scores, 'fun'), color: 'bg-[#E1C699]' }, { label: '安全防线', val: getScorePct(scores, 'safety'), color: 'bg-[#A9B0C3]' }, { label: '绝对自由', val: getScorePct(scores, 'freedom'), color: 'bg-[#91A8A6]' }, { label: '社会贡献', val: getScorePct(scores, 'contribute'), color: 'bg-[#C59B88]' }];
    const amplifyBars = (items: typeof top) => {
      const vals = items.map((item) => item.val);
      const min = Math.min(...vals);
      const max = Math.max(...vals);
      const range = Math.max(1, max - min);
      return items.map((item) => {
        const normalized = (item.val - min) / range;
        return { ...item, displayHeight: Math.round(24 + normalized * 76) };
      });
    };
    const topBars = amplifyBars(top);
    const bottomBars = amplifyBars(bottom);
    const BarRow = ({ data: d }: { data: Array<{ label: string; val: number; color: string; displayHeight: number }> }) => (
      <div className="flex justify-between items-end h-32 border-b-2 border-gray-800 pb-2 mb-2 gap-1">
        {d.map((item, idx) => (
          <div key={idx} className="flex flex-col items-center flex-1">
            <div className={`w-full max-w-[32px] ${item.color} rounded-t`} style={{ height: `${item.displayHeight}%`, minHeight: '8px' }}>
              <div className="text-[9px] text-white font-bold text-center mt-0.5">{item.val}%</div>
            </div>
            <div className="mt-2 text-[10px] font-bold text-gray-700 text-center">{item.label}</div>
          </div>
        ))}
      </div>
    );
    return (
      <div className="p-4 pb-24">
        <div ref={reportRef} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <h3 className="text-center text-gray-500 text-sm mb-8 tracking-widest uppercase font-bold">潜意识核心驱动力剖析</h3>
          <BarRow data={topBars} /><BarRow data={bottomBars} />
          <QRBanner qrDataUrl={qrDataUrl} />
        </div>
        <button onClick={downloadImage} className="fixed bottom-6 left-6 right-6 max-w-md mx-auto bg-blue-600 hover:bg-blue-700 text-white rounded-full py-4 font-bold shadow-lg flex items-center justify-center"><Download size={20} className="mr-2" />保存报告原图</button>
      </div>
    );
  }
  if (testId === 'role') {
    const data = [
      { subject: 'Dom（支配）', A: getScorePct(scores, 'dom') },
      { subject: 'Sub（臣服）', A: getScorePct(scores, 'sub') },
      { subject: 'Sadism（施虐）', A: getScorePct(scores, 'sadism') },
      { subject: 'Masochism（受虐）', A: getScorePct(scores, 'masochism') },
      { subject: 'Brat（调皮型）', A: getScorePct(scores, 'brat') },
      { subject: 'Primal（原始型）', A: getScorePct(scores, 'primal') },
      { subject: 'Switch（双型）', A: getScorePct(scores, 'switch') },
      { subject: 'Vanilla（纯爱型）', A: getScorePct(scores, 'vanilla') },
    ];
    const sorted = [...data].sort((a, b) => b.A - a.A); const dominant = sorted[0].subject.split(' ')[0];
    return (
      <div className="p-4 pb-24">
        <div ref={reportRef} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <div className="text-center mb-6"><h3 className="text-gray-500 text-sm font-bold mb-2">您的核心心理倾向</h3><h1 className="text-4xl font-black text-rose-500">{dominant}</h1></div>
          <div className="h-60 w-full mb-6"><ResponsiveContainer width="100%" height="100%"><RadarChart cx="50%" cy="50%" outerRadius="65%" data={data}><PolarGrid stroke="#e5e7eb" /><PolarAngleAxis dataKey="subject" tick={{ fill: '#4b5563', fontSize: 10 }} /><PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} /><Radar name="得分" dataKey="A" stroke="#f43f5e" strokeWidth={2} fill="#fb7185" fillOpacity={0.5} dot={{ r: 3, fill: '#be123c' }} /></RadarChart></ResponsiveContainer></div>
          <div className="space-y-2">{data.map((item, idx) => (<div key={idx} className="flex items-center text-sm"><div className="min-w-[9.5rem] shrink-0 text-gray-700 font-bold text-xs whitespace-nowrap">{item.subject}</div><div className="flex-1 bg-rose-50 rounded-full h-3 mx-2 relative overflow-hidden min-w-0"><div className="absolute left-0 top-0 bottom-0 bg-rose-400 rounded-full" style={{ width: `${item.A}%` }}></div></div><div className="w-10 shrink-0 text-right text-rose-600 font-black text-sm">{item.A}%</div></div>))}</div>
          <QRBanner qrDataUrl={qrDataUrl} />
        </div>
        <button onClick={downloadImage} className="fixed bottom-6 left-6 right-6 max-w-md mx-auto bg-blue-600 hover:bg-blue-700 text-white rounded-full py-4 font-bold shadow-lg flex items-center justify-center"><Download size={20} className="mr-2" />保存报告原图</button>
      </div>
    );
  }
  if (testId === 'abo') {
    const data = [{ subject: 'Alpha', A: getScorePct(scores, 'alpha'), color: '#dc2626' }, { subject: 'Beta', A: getScorePct(scores, 'beta'), color: '#059669' }, { subject: 'Omega', A: getScorePct(scores, 'omega'), color: '#2563eb' }];
    const sorted = [...data].sort((a, b) => b.A - a.A); const topAbo = sorted[0];
    const descMap: Record<string, string> = { Alpha: '天生的统治者与征服者，骨子里流淌着野心与强烈的领地意识。', Beta: '完美的中坚力量与和平主义者，情绪稳定，理性务实。', Omega: '共情能力的极致体现，内心柔软敏感且富有奉献精神。' };
    return (
      <div className="p-4 pb-24">
        <div ref={reportRef} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <div className="text-center mb-6"><h3 className="text-gray-500 text-sm font-bold mb-2">你的灵魂心理性别是</h3><h1 className="text-5xl font-black" style={{ color: topAbo.color }}>{topAbo.subject}</h1></div>
          <div className="h-52 w-full mb-6"><ResponsiveContainer width="100%" height="100%"><RadarChart cx="50%" cy="50%" outerRadius="65%" data={data}><PolarGrid stroke="#e5e7eb" /><PolarAngleAxis dataKey="subject" tick={{ fill: '#4b5563', fontSize: 12 }} /><PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} /><Radar name="得分" dataKey="A" stroke={topAbo.color} strokeWidth={3} fill={topAbo.color} fillOpacity={0.3} dot={{ r: 4, fill: topAbo.color }} /></RadarChart></ResponsiveContainer></div>
          <div className="bg-gray-800 text-white rounded-xl p-5"><p className="text-sm leading-relaxed text-gray-200">{descMap[topAbo.subject] || ''}</p></div>
          <QRBanner qrDataUrl={qrDataUrl} />
        </div>
        <button onClick={downloadImage} className="fixed bottom-6 left-6 right-6 max-w-md mx-auto bg-blue-600 hover:bg-blue-700 text-white rounded-full py-4 font-bold shadow-lg flex items-center justify-center"><Download size={20} className="mr-2" />保存报告原图</button>
      </div>
    );
  }

  return (
    <div className="p-4 pb-24">
      <div ref={reportRef} className="bg-white rounded-2xl p-6 shadow-sm">
        <h2 className="text-xl font-bold text-gray-800">{meta.title}</h2>
        <p className="text-gray-500 mt-2">暂无详细结果页</p>
      </div>
    </div>
  );
}
